import React, { useEffect, useMemo, useState, useRef } from 'react';
import authService from '../../services/authService';
import './AiPlayground.css';
import Toast from '../Toast';
import HelpModal from '../HelpModal';
import PromptGeneratorModal from '../PromptGeneratorModal';
import { useTier } from '../../hooks/useTier';

// Language level options (moved outside component to prevent recreation on every render)
const pgLanguageLevelOptions = [
	{ value: 'Advanced', prompt: 'sophisticated vocabulary and complex ideas' },
	{ value: 'Intermediate', prompt: 'everyday language that most people understand' },
	{ value: 'Elementary', prompt: 'simple words and short sentences' }
];

const AiPlayground = () => {
    const [providers, setProviders] = useState([]);
	const [providerKey, setProviderKey] = useState('OpenAI');
	const [model, setModel] = useState('');
	const [prompt, setPrompt] = useState('');
	const [output, setOutput] = useState('');
	const [thread, setThread] = useState([]);
	const [usage, setUsage] = useState(null);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState('');

	const [tokensRemaining, setTokensRemaining] = useState(null);
	const [sessionRequests, setSessionRequests] = useState(0);
	const [sessionTokensIn, setSessionTokensIn] = useState(0);
	const [sessionTokensOut, setSessionTokensOut] = useState(0);
	const [lastRequestCostUsd, setLastRequestCostUsd] = useState(null);
	const [sessionTotalCostUsd, setSessionTotalCostUsd] = useState(0);
	const [ioText, setIoText] = useState('');
	const [showRecall, setShowRecall] = useState(false);
	const [savedIos, setSavedIos] = useState([]);
	const [pricing, setPricing] = useState([]);
	const [userFirstName, setUserFirstName] = useState('');
	const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmToast, setConfirmToast] = useState({ show: false, message: '', onConfirm: null, onCancel: null });
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [showResetConfirm, setShowResetConfirm] = useState(false);
	const [showRenameModal, setShowRenameModal] = useState(false);
	const [renameTitle, setRenameTitle] = useState('');
	const [renameId, setRenameId] = useState(null);
	const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
	const [pendingNavTo, setPendingNavTo] = useState(null);
	const [pgHelpOpen, setPgHelpOpen] = useState(false);
	const [showPromptGeneratorModal, setShowPromptGeneratorModal] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [isStudioMode, setIsStudioMode] = useState(() => {
		try {
			return localStorage.getItem('ai_studioMode') === '1';
		} catch {
			return false;
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem('ai_studioMode', isStudioMode ? '1' : '0');
		} catch {}
	}, [isStudioMode]);
	const [saveTitle, setSaveTitle] = useState('');
	const [optimizing, setOptimizing] = useState(false);
    const abortControllerRef = useRef(null);
	const [optimizeLocked, setOptimizeLocked] = useState(false);
	const [usageRefreshTick, setUsageRefreshTick] = useState(0);
	// Multi-send (Pro only)
	const [showMultiSend, setShowMultiSend] = useState(false);
	const [msSelected, setMsSelected] = useState([]); // [{ provider, model, label }]
	const [msPending, setMsPending] = useState(false);
	const [chatMode, setChatMode] = useState(() => (localStorage.getItem('ai_chatMode') === '1'));
    const [isPaidUser, setIsPaidUser] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [isTeamEntAdmin, setIsTeamEntAdmin] = useState(false); // Set but may be used in future
	
	// Tier-based authorization
	const { tierInfo, canAccessFeature, getFeatureDenialMessage, refreshTier, tierChangeNotification, clearTierChangeNotification } = useTier();
	
	// Display tier change notifications
	useEffect(() => {
		if (tierChangeNotification) {
			setToast({
				show: true,
				type: tierChangeNotification.type,
				message: tierChangeNotification.message
			});
			clearTierChangeNotification();
		}
	}, [tierChangeNotification, clearTierChangeNotification]);
    const [ocrIfEmpty, setOcrIfEmpty] = useState(() => {
      try {
        const v = localStorage.getItem('ai_ocrIfEmpty');
        return v === null ? true : v === '1';
      } catch { return true; }
    });
    const [ocrLangs, setOcrLangs] = useState(() => {
      try { return localStorage.getItem('ai_ocrLangs') || ''; } catch { return ''; }
    });
    const [ocrMenuOpen, setOcrMenuOpen] = useState(false);
    useEffect(() => {
      try { localStorage.setItem('ai_ocrIfEmpty', ocrIfEmpty ? '1' : '0'); } catch {}
    }, [ocrIfEmpty]);
    useEffect(() => {
      try { if (ocrLangs != null) localStorage.setItem('ai_ocrLangs', String(ocrLangs)); } catch {}
    }, [ocrLangs]);
    const [showOverride, setShowOverride] = useState(false);
    // Tuning parameters (Studio mode only)
    const [temperature, setTemperature] = useState(() => {
      try {
        const v = localStorage.getItem('ai_temperature');
        return v ? parseFloat(v) : 0.7;
      } catch {
        return 0.7;
      }
    });
    const [topP, setTopP] = useState(() => {
      try {
        const v = localStorage.getItem('ai_topP');
        return v ? parseFloat(v) : 0.9;
      } catch {
        return 0.9;
      }
    });
    const [tuningMenuOpen, setTuningMenuOpen] = useState(false);
    const [temperatureScale, setTemperatureScale] = useState(2); // 0-2 scale by default
    useEffect(() => {
      try { localStorage.setItem('ai_temperature', String(temperature)); } catch {}
    }, [temperature]);
    useEffect(() => {
      try { localStorage.setItem('ai_topP', String(topP)); } catch {}
    }, [topP]);
    const [ovTokens, setOvTokens] = useState(8192);
    const [ovMode, setOvMode] = useState('time'); // 'time' | 'uses'
    const [ovHours, setOvHours] = useState(48);
    const [ovUses, setOvUses] = useState(5);
    const [ovPending, setOvPending] = useState(false);
	// CCO (cutoff) is a floor for remaining balance during Chat
	const [chatCcoCents, setChatCcoCents] = useState(() => {
		const v = localStorage.getItem('ai_chatCcoCents');
		return v ? Number(v) : null;
	});
	// Budget percentage used to compute CCO from current balance when enabling/editing Chat
	const [chatPct, setChatPct] = useState(() => {
		const v = localStorage.getItem('ai_chatPct');
		return v ? Number(v) : 50;
	});
	// Context window: number of message pairs (default 15 pairs = 30 messages)
	const [chatContextWindow, setChatContextWindow] = useState(() => {
		const v = localStorage.getItem('ai_chatContextWindow');
		return v ? Number(v) : 15;
	});
	// Temporary context expansion for "load more" feature
	const [tempContextExpansion, setTempContextExpansion] = useState(0);
	// Track archived message count for UI display
	const [archivedCount, setArchivedCount] = useState(0);
	const [showChatModal, setShowChatModal] = useState(false);
	const [chatBalanceCents, setChatBalanceCents] = useState(null);
	const [showSaveWarningModal, setShowSaveWarningModal] = useState(false);
	const [pendingSaveTitle, setPendingSaveTitle] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [estCostCents, setEstCostCents] = useState(null); // Set but not currently displayed in UI
	// eslint-disable-next-line no-unused-vars
	const [estLoading, setEstLoading] = useState(false); // Set but not currently displayed in UI
  // Local preflight: estimate input tokens and simple cap for warnings
  const [preflightWarn, setPreflightWarn] = useState(null); // string | null
  const [sendBlocked, setSendBlocked] = useState(false);

	// Attachments (file picker via chip) - must be declared before effects that read it
	const [attachments, setAttachments] = useState([]); // [{ name, type, size, text, include, useExtract, useVision, pageStart, pageEnd }]
	const [showAttachDrawer, setShowAttachDrawer] = useState(false);
	const fileInputRef = useRef(null);

  // Recompute local preflight on prompt/model/attachments/MaxOut changes
  // Use useMemo to prevent unnecessary recalculations and debounce for typing
  useEffect(() => {
    // Debounce the check to prevent flickering during typing
    const timeoutId = setTimeout(() => {
      try {
        const text = (prompt || '').trim();
        // Only consider included attachments
        const included = (attachments || []).filter(a => a && a.include !== false);
        let charCount = text.length + included.reduce((sum, a) => sum + ((a.text || '').length), 0);
        let estInput = Math.ceil(Math.max(0, charCount) / 4.0);
        // Per-image heuristic
        const provLower = String(providerKey || '').toLowerCase();
        const perImg = (provLower === 'openai') ? 300 : 250;
        let imgs = 0;
        try {
          imgs = included.filter(a => {
            const mt = String((a && (a.MimeType || a.mimeType || a.type)) || '').toLowerCase();
            return !!a.Base64 && mt.startsWith('image/');
          }).length;
        } catch {}
        estInput += imgs * perImg;
        // Input cap = 3x MaxOut (fallback rule)
        let maxOut = 4096; try { const v = localStorage.getItem('ai_defaultMaxTokens'); if (v) maxOut = parseInt(v,10) || maxOut; } catch {}
        const inputCap = maxOut * 3;
        
        // Calculate new values
        const newBlocked = estInput > inputCap;
        const newWarn = newBlocked 
          ? 'Inputs exceed cap. Remove images/text or lower Max Output.'
          : (estInput > Math.floor(inputCap * 0.9))
            ? 'Inputs near cap. Consider fewer images or lower Max Output.'
            : null;
        
        // Only update if values actually changed to prevent flickering
        setSendBlocked(prev => prev !== newBlocked ? newBlocked : prev);
        setPreflightWarn(prev => prev !== newWarn ? newWarn : prev);
      } catch {
        setPreflightWarn(null);
        setSendBlocked(false);
      }
    }, 150); // 150ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [prompt, attachments, providerKey, model]);

	// (moved up) Attachments state

	// Embedded Prompt Generator (simplified) state
	const [pgCategories, setPgCategories] = useState([]);
	const [pgStyleTemplates, setPgStyleTemplates] = useState([]);
	const [pgOutcomeTemplates, setPgOutcomeTemplates] = useState([]);
	const [pgSelectedCategory, setPgSelectedCategory] = useState('');
	const [pgSelectedStyle, setPgSelectedStyle] = useState('');
	const [pgSelectedStyleTemplate, setPgSelectedStyleTemplate] = useState(null);
	const [pgSelectedOutcomeTemplate, setPgSelectedOutcomeTemplate] = useState(null);
	const [pgTopicInput] = useState('');
	const [pgSubtopicInput] = useState('');
	const [, setPgGeneratedPrompt] = useState('');
	const [pgSelectedLanguage] = useState('English');
	const [pgSelectedLanguageLevel] = useState('Advanced');
	const [, setPgTopicSuggestions] = useState([]);
	const [, setPgSubtopicSuggestions] = useState([]);
	const [pgSelectedTopicId] = useState(null);
    const topicInputRef = useRef(null);
    const allowUnloadRef = useRef(false);

	// Determine temperature scale based on provider/model
	useEffect(() => {
		if (providerKey && model) {
			const provLower = String(providerKey || '').toLowerCase();
			const modelLower = String(model || '').toLowerCase();
			// Anthropic uses 0-1 scale, others use 0-2 scale (backend multiplies by 2)
			if (provLower === 'anthropic' || modelLower.includes('claude')) {
				setTemperatureScale(1);
			} else {
				setTemperatureScale(2);
			}
		}
	}, [providerKey, model]);

	// Reset tuning parameters when model changes
	useEffect(() => {
		setTemperature(0.7);
		setTopP(0.9);
		setTuningMenuOpen(false);
	}, [providerKey, model]);

	// One-time debug log of finalized provider/model once both are set
	const loggedDefaultsRef = useRef(false);
	useEffect(() => {
		if (!loggedDefaultsRef.current && providerKey && model) {
			try { console.log('AI active provider:', providerKey, 'AI active model:', model); } catch {}
			loggedDefaultsRef.current = true;
		}
	}, [providerKey, model]);

	const loadSaves = () => {
		try {
			const raw = localStorage.getItem('aiIoSaves') || '[]';
			const items = JSON.parse(raw);
			setSavedIos(Array.isArray(items) ? items : []);
		} catch {
			setSavedIos([]);
		}
	};

	// Track unsaved work across navigation
	useEffect(() => {
		try {
			const hasUnsaved = (String(prompt || '').trim().length > 0) || (String(ioText || '').trim().length > 0);
			sessionStorage.setItem('ai_unsaved', hasUnsaved ? '1' : '0');
		} catch {}
	}, [prompt, ioText]);

    // Removed native beforeunload prompt to avoid browser "says" dialogs

	const refreshSavedList = async () => {
		try {
			const t = authService.getToken();
			const res = await fetch('/api/aiiosaves', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
			if (res.ok) {
				const items = await res.json();
				setSavedIos(Array.isArray(items) ? items : []);
			}
		} catch {}
	};

	// Keep global header visible on this page
	useEffect(() => {
		try { console.log('[AI] AiPlayground mounted'); } catch {}
	}, []);

	useEffect(() => {
		const load = async () => {
			try {
				// Paste incoming prompt from PromptGenerator
				try {
					const incoming = sessionStorage.getItem('ai_paste_prompt');
					if (typeof incoming === 'string') {
						setPrompt('');
						setIoText('');
						setTimeout(() => {
							setPrompt(incoming);
							try { sessionStorage.removeItem('ai_paste_prompt'); } catch {}
						}, 0);
					}
				} catch {}
				try { console.log('[AI] Loading models...'); } catch {}
				const resp = await fetch('/api/ai/models');
				if (!resp.ok) throw new Error(`Failed to load models (${resp.status})`);
				const data = await resp.json();
				const items = (data?.providers || []).map(p => ({
					key: p.provider,
					name: p.provider,
					defaultModel: p.defaultModel,
					allowed: p.allowedModels || []
				}));
				setProviders(items);
				try { console.log('[AI] Providers loaded:', items.length, items.map(i=>i.key)); } catch {}
				if (items.length > 0) {
					const savedProv = localStorage.getItem('ai_provider');
					let savedModel = localStorage.getItem('ai_model');
					// Migrate legacy Perplexity sonar models to Together llama-4-scout
					if (savedModel && /^sonar/i.test(savedModel)) {
						savedModel = 'llama-4-scout';
						try { localStorage.setItem('ai_model', savedModel); } catch {}
					}
					const openAi = items.find(p => String(p.key).toLowerCase() === 'openai');
					// Fetch server-side defaults
					let prefProv = null, prefModel = null, prefMaxTok = null;
					try {
						const t = authService.getToken && authService.getToken();
						const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
                        if (res.ok) {
                            const prefs = await res.json();
                            // Handle both PascalCase and camelCase JSON
                            prefProv = (prefs?.DefaultProvider ?? prefs?.defaultProvider) || null;
                            prefModel = (prefs?.DefaultModel ?? prefs?.defaultModel) || null;
                            prefMaxTok = (prefs?.DefaultMaxOutputTokens ?? prefs?.defaultMaxOutputTokens) || null;
                            try { const cap = prefs?.PerRequestCapCents ?? prefs?.perRequestCapCents; if (typeof cap === 'number') setCapCents(cap); } catch {}
                            // Tier-based clamping of default max tokens
                            try {
                              const roles = (prefs?.UserRoles || []).map(r => String(r).toLowerCase());
                              const isTeam = roles.includes('teamuser') || roles.includes('teamadmin');
                              const isEnt = roles.includes('enterpriseuser') || roles.includes('enterpriseadmin');
                              const isAdmin = roles.includes('admin');
                              const paid = roles.includes('paiduser') || isAdmin || isTeam || isEnt;
                              const capTok = (isAdmin || isTeam || isEnt) ? 32768 : (paid ? 8192 : 1024);
                              if (!prefMaxTok || prefMaxTok > capTok) prefMaxTok = capTok;
                            } catch {}
				            try { console.log('[AI] Prefs fetched:', { prefProv, prefModel, prefMaxTok }); } catch {}
                        }
					} catch {}
                    // Choose provider/model consistently with server prefs.
                    const lc = (s) => String(s||'').toLowerCase();
                    const findProvByKey = (val) => items.find(p => lc(p.key) === lc(val)) || null;
					const modelMatches = (candidate, target) => {
						const a = lc(candidate); const b = lc(target);
						if (!b) return false;
						if (a === b) return true;
						if (a.startsWith(b) || b.startsWith(a)) return true;
						return a.includes(b) || b.includes(a);
					};
					const normalizeModel = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
					const chooseBestAllowedModel = (allowedList, target) => {
						const tn = normalizeModel(target);
						if (!tn) return null;
						// Exact normalized match first
						const exact = (allowedList || []).find(m => normalizeModel(m) === tn);
						if (exact) return exact;
						// Then pick the closest length candidate where one contains the other
						const cands = (allowedList || [])
							.map(m => ({ m, n: normalizeModel(m) }))
							.filter(x => x.n.includes(tn) || tn.includes(x.n));
						if (cands.length === 0) return null;
						cands.sort((a, b) => {
							const ad = Math.abs(a.n.length - tn.length);
							const bd = Math.abs(b.n.length - tn.length);
							if (ad !== bd) return ad - bd; // closest length first
							return b.n.length - a.n.length; // prefer longer on tie
						});
						return cands[0].m;
					};
                    const findProvByModel = (m) => items.find(p => (p.allowed||[]).some(x => modelMatches(x, m))) || null;
                    const prefProvObj = findProvByKey(prefProv);
                    const savedProvObj = findProvByKey(savedProv);
                    // If server default model is set, prefer the provider that offers it; else prefer server provider
                    let chosenProv = prefModel ? (findProvByModel(prefModel) || prefProvObj || savedProvObj) : (prefProvObj || savedProvObj);
                    if (!chosenProv) chosenProv = (openAi || items[0]);
                    const allowed = chosenProv.allowed || [];
					let defModel = null;
					// Prefer best-match against server default model; else against saved model
					const bestPrefModel = chooseBestAllowedModel(allowed, prefModel);
					const bestSavedModel = chooseBestAllowedModel(allowed, savedModel);
					if (bestPrefModel) defModel = bestPrefModel;
					else if (bestSavedModel) defModel = bestSavedModel;
                    else defModel = (chosenProv.defaultModel || allowed[0] || '');
					// Log the computed defaults on initial load for debugging
					try { console.log('AI default provider:', (chosenProv && chosenProv.key) || '', 'AI default model:', defModel || ''); } catch {}
					// If server has no saved provider/model yet, seed them from the resolved selection
					try {
						if ((!prefProv || String(prefProv).trim()==='') && (!prefModel || String(prefModel).trim()==='') && chosenProv && defModel) {
							const t3 = authService.getToken && authService.getToken();
							const r3 = await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t3?{ Authorization:`Bearer ${t3}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultProvider: chosenProv.key, DefaultModel: defModel }) });
							try { console.log('[AI] Seeded server defaults:', r3 && r3.ok); } catch {}
						}
					} catch {}
                    setProviderKey(chosenProv.key);
                    setModel(defModel);
					if (prefMaxTok && Number.isFinite(prefMaxTok)) {
						try { localStorage.setItem('ai_defaultMaxTokens', String(prefMaxTok)); } catch {}
					}
                    localStorage.setItem('ai_provider', chosenProv.key);
					if (defModel) localStorage.setItem('ai_model', defModel);
				}
			} catch (e) {
				try { console.error('[AI] Failed to load models:', e); } catch {}
				setError(e.message || 'Failed to load models');
			}
		};
		load();
		// fetch user info for personalized labels
		(async () => {
			try {
				const u = await authService.getUserInfo();
				const first = (u && (u.firstName || (u.name ? String(u.name).split(' ')[0] : ''))) || '';
				setUserFirstName(first);
                const roles = (u && u.roles) ? u.roles.map(r=>String(r).toLowerCase()) : [];
                const isTeam = roles.includes('teamuser') || roles.includes('teamadmin');
                const isEnt = roles.includes('enterpriseuser') || roles.includes('enterpriseadmin');
                const isAdmin = roles.includes('admin');
                const hasPaidRole = roles.includes('paiduser');
                // Determine effective paid: org/admin always paid; individual paid only if balance > 0
                let effectivePaid = (isAdmin || isTeam || isEnt);
                if (!effectivePaid && hasPaidRole) {
                    try {
                        const t2 = authService.getToken && authService.getToken();
                        const rb = await fetch('/api/billing/balance', { credentials: 'include', cache: 'no-store', headers: t2 ? { Authorization: `Bearer ${t2}` } : {} });
                        if (rb.ok) {
                            const ct = rb.headers.get('content-type') || '';
                            const j = ct.includes('application/json') ? (await rb.json()) : null;
                            const bal = typeof j?.balanceCents === 'number' ? j.balanceCents : 0;
                            effectivePaid = bal > 0;
                        }
                    } catch { effectivePaid = false; }
                }
                setIsPaidUser(effectivePaid);
                setIsTeamEntAdmin(isAdmin || isTeam || isEnt);
                // Clamp stored default max tokens to tier caps
                try {
                    const cap = (isAdmin || isTeam || isEnt) ? 32768 : (effectivePaid ? 8192 : 1024);
                    const v = localStorage.getItem('ai_defaultMaxTokens');
                    if (!v || (parseInt(v,10) || 0) > cap) {
                        localStorage.setItem('ai_defaultMaxTokens', String(cap));
                    }
                } catch {}
				if (!effectivePaid) { setChatMode(false); localStorage.setItem('ai_chatMode','0'); }
			} catch {}
		})();
		// load pricing for client-side cost estimation
		(async () => {
			try {
				const t = authService.getToken();
				const res = await fetch('/api/aipricing/active', { headers: t ? { Authorization: `Bearer ${t}` } : {} });
				if (res.ok) {
					const items = await res.json();
					setPricing(Array.isArray(items) ? items : []);
				}
			} catch {}
		})();
		// load PG categories
		(async () => {
			try {
				const res = await fetch('/api/categories/active');
				if (res.ok) {
					const data = await res.json();
					setPgCategories(Array.isArray(data) ? data : []);
					// After categories load, auto-select user's favorite category if set
					try {
						const t = authService.getToken && authService.getToken();
						const r = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
                        if (r.ok) {
                            const p = await r.json();
							let favId = (p && (p.FavoriteCategoryId ?? p.favoriteCategoryId)) || null;
                            try { const cap = p?.PerRequestCapCents ?? p?.perRequestCapCents; if (typeof cap === 'number') setCapCents(cap); } catch {}
							if (!favId) {
								try { const v = localStorage.getItem('favoriteCategoryId'); if (v) favId = parseInt(v, 10) || null; } catch {}
							}
							if (favId && (Array.isArray(data) && data.some(c => String(c.id) === String(favId)))) {
								setPgSelectedCategory(String(favId));
							}
						}
					} catch {}
				}
			} catch {}
		})();
		loadSaves();
	}, []);

	// Listen for global leave confirmation requests from header
	useEffect(() => {
		const onAsk = () => {
			setShowLeaveConfirm(true);
			try { setPendingNavTo(sessionStorage.getItem('ai_nav_confirm_to') || null); } catch {}
		};
		window.addEventListener('ai:confirm-leave', onAsk);
		return () => window.removeEventListener('ai:confirm-leave', onAsk);
	}, []);

	// Warn on browser unload/refresh if there are unsaved changes
    useEffect(() => {
        const beforeUnload = (e) => {
            try {
                if (allowUnloadRef.current) return; // allow silent navigation after user confirms in custom modal
                const hasUnsaved = sessionStorage.getItem('ai_unsaved') === '1';
                const path = String(window.location.pathname || '');
                if (path.startsWith('/ai') && hasUnsaved) {
                    e.preventDefault();
                    e.returnValue = '';
                    return '';
                }
            } catch {}
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, []);

	// Intercept history navigation (back/forward and programmatic push/replace) when leaving /ai with unsaved changes
	useEffect(() => {
		const dispatchConfirm = (to) => {
			try { sessionStorage.setItem('ai_nav_confirm_to', to || '/'); } catch {}
			const ev = new CustomEvent('ai:confirm-leave');
			window.dispatchEvent(ev);
		};

		const wrapHistoryMethod = (methodName) => {
			const orig = window.history[methodName];
			return function() {
				try {
					const path = String(window.location.pathname || '');
					const hasUnsaved = sessionStorage.getItem('ai_unsaved') === '1';
					const url = (arguments && typeof arguments[2] === 'string') ? arguments[2] : null;
					if (path.startsWith('/ai') && hasUnsaved && url && !String(url).startsWith('/ai')) {
						dispatchConfirm(String(url));
						return; // block navigation
					}
				} catch {}
				return orig.apply(window.history, arguments);
			};
		};

		const onPopState = () => {
			try {
				const hasUnsaved = sessionStorage.getItem('ai_unsaved') === '1';
				const path = String(window.location.pathname || '');
				if (!path.startsWith('/ai') && hasUnsaved) {
					// bring user back and show confirm
					window.history.pushState(null, '', '/ai');
					dispatchConfirm(path);
				}
			} catch {}
		};

		const restore = {
			pushState: window.history.pushState,
			replaceState: window.history.replaceState
		};
		window.history.pushState = wrapHistoryMethod('pushState');
		window.history.replaceState = wrapHistoryMethod('replaceState');
		window.addEventListener('popstate', onPopState);

		return () => {
			window.history.pushState = restore.pushState;
			window.history.replaceState = restore.replaceState;
			window.removeEventListener('popstate', onPopState);
		};
	}, []);
	useEffect(() => {
		const run = async () => {
			if (!pgSelectedCategory) {
				setPgStyleTemplates([]);
				setPgOutcomeTemplates([]);
				setPgSelectedStyle('');
				setPgSelectedStyleTemplate(null);
				setPgSelectedOutcomeTemplate(null);
				return;
			}
			try {
				const [sRes, oRes] = await Promise.all([
					fetch(`/api/promptgenerator/style-templates?categoryId=${pgSelectedCategory}`),
					fetch(`/api/promptgenerator/outcome-templates?categoryId=${pgSelectedCategory}`)
				]);
				if (sRes.ok) setPgStyleTemplates(await sRes.json()); else setPgStyleTemplates([]);
				if (oRes.ok) setPgOutcomeTemplates(await oRes.json()); else setPgOutcomeTemplates([]);
				setPgSelectedStyle('');
				setPgSelectedStyleTemplate(null);
				setPgSelectedOutcomeTemplate(null);
			} catch {
				setPgStyleTemplates([]);
				setPgOutcomeTemplates([]);
			}
		};
		run();
	}, [pgSelectedCategory]);

	useEffect(() => {
		// update selected templates when style changes
		if (!pgSelectedStyle) {
			setPgSelectedStyleTemplate(null);
			setPgSelectedOutcomeTemplate(null);
			return;
		}
		const st = (pgStyleTemplates || []).find(t => String(t.id) === String(pgSelectedStyle));
		setPgSelectedStyleTemplate(st || null);
		if (st) {
			const match = (pgOutcomeTemplates || []).find(t => t.categoryId === st.categoryId && t.sortOrder === st.sortOrder);
			setPgSelectedOutcomeTemplate(match || null);
		} else {
			setPgSelectedOutcomeTemplate(null);
		}
	}, [pgSelectedStyle, pgStyleTemplates, pgOutcomeTemplates]);

	useEffect(() => {
		// rebuild prompt when dependencies change
		let text = '';
		if (pgSelectedStyleTemplate && pgSelectedStyleTemplate.templateText) {
			text += pgSelectedStyleTemplate.templateText;
		}
		if (pgTopicInput.trim()) {
			if (text) text += '\n\n';
			if (pgSubtopicInput.trim()) {
				text += `Focus on ${pgTopicInput.trim()}, specifically addressing ${pgSubtopicInput.trim()}.`;
			} else {
				text += `Focus on ${pgTopicInput.trim()}.`;
			}
		}
		if (pgSelectedOutcomeTemplate && pgSelectedOutcomeTemplate.templateText) {
			if (text) text += '\n\n';
			text += pgSelectedOutcomeTemplate.templateText;
		}
		// Language and level specifications if not defaults
		if (pgSelectedLanguage !== 'English' || pgSelectedLanguageLevel !== 'Advanced') {
			if (text) text += '\n\n';
			const levelPrompt = (pgLanguageLevelOptions.find(l => l.value === pgSelectedLanguageLevel)?.prompt) || 'sophisticated vocabulary and complex ideas';
			text += `Write in ${pgSelectedLanguage}. Use ${levelPrompt}.`;
		}
		setPgGeneratedPrompt(text);
		// Sync preview into main Prompt Input
		setPrompt(text);
	}, [pgSelectedStyleTemplate, pgSelectedOutcomeTemplate, pgTopicInput, pgSubtopicInput, pgSelectedLanguage, pgSelectedLanguageLevel]);

	// Debounced topic suggestions (3+ chars)
	useEffect(() => {
		const id = setTimeout(async () => {
			const q = (pgTopicInput || '').trim();
			if (q.length >= 3) {
				try {
					const res = await fetch(`/api/topics/search?q=${encodeURIComponent(q)}`);
					if (res.ok) {
						const data = await res.json();
						setPgTopicSuggestions(Array.isArray(data) ? data : []);
					} else {
						setPgTopicSuggestions([]);
					}
				} catch { setPgTopicSuggestions([]); }
			} else {
				setPgTopicSuggestions([]);
			}
		}, 300);
		return () => clearTimeout(id);
	}, [pgTopicInput]);

	// Debounced subtopic suggestions (3+ chars when a topic is selected)
	useEffect(() => {
		const id = setTimeout(async () => {
			const q = (pgSubtopicInput || '').trim();
			if (pgSelectedTopicId && q.length >= 3) {
				try {
					const res = await fetch(`/api/promptgenerator/search-subtopics?topicId=${pgSelectedTopicId}&q=${encodeURIComponent(q)}`);
					if (res.ok) {
						const data = await res.json();
						setPgSubtopicSuggestions(Array.isArray(data) ? data : []);
					} else {
						setPgSubtopicSuggestions([]);
					}
				} catch { setPgSubtopicSuggestions([]); }
			} else if (pgSelectedTopicId && q.length === 0) {
				try {
					const res = await fetch(`/api/promptgenerator/subtopics?topicId=${pgSelectedTopicId}`);
					if (res.ok) {
						const data = await res.json();
						setPgSubtopicSuggestions(Array.isArray(data) ? data : []);
					} else {
						setPgSubtopicSuggestions([]);
					}
				} catch { setPgSubtopicSuggestions([]); }
			} else {
				setPgSubtopicSuggestions([]);
			}
		}, 300);
		return () => clearTimeout(id);
	}, [pgSubtopicInput, pgSelectedTopicId]);

	// const insertGeneratedPrompt = () => {}; // Reserved for future use

	// Handle selection from Get Help modal (mirrors PromptGenerator behavior)
	const handlePgHelpSelect = async (selectedPurpose) => {
		try {
			if (selectedPurpose && selectedPurpose.suggestedCategory) {
				const categoryId = String(selectedPurpose.suggestedCategory.id);
				if (selectedPurpose.suggestedStyleTemplate && selectedPurpose.suggestedOutcomeTemplate) {
					const styleId = String(selectedPurpose.suggestedStyleTemplate.id);
					setPgSelectedStyleTemplate(selectedPurpose.suggestedStyleTemplate);
					setPgSelectedOutcomeTemplate(selectedPurpose.suggestedOutcomeTemplate);
					setPgSelectedCategory(categoryId);
					setTimeout(() => setPgSelectedStyle(styleId), 200);
				} else {
					setPgSelectedStyle('');
					setPgSelectedStyleTemplate(null);
					setPgSelectedOutcomeTemplate(null);
					setPgSelectedCategory(categoryId);
				}
				setToast({ show: true, type: 'success', message: 'Category selected.' });
				// Auto-advance to Topic input
				setTimeout(() => {
					try {
						if (topicInputRef && topicInputRef.current) {
							topicInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
							try { topicInputRef.current.focus(); } catch {}
						}
					} catch {}
				}, 400);
			}
		} catch (e) {
			setToast({ show: true, type: 'error', message: 'Failed to apply selection.' });
		} finally {
			setPgHelpOpen(false);
		}
	};
	const formatCostUsd = (usd) => {
		if (usd == null || !isFinite(usd)) return '--';
		if (usd >= 1) return `$${usd.toFixed(2)}`;
		const cents = usd * 100;
		if (cents >= 0.1) return `${cents.toFixed(2)}¢`;
		if (cents > 0) return '< 0.10¢';
		return '—';
	};

	// Extract topic/subtopic only when the text itself starts with "Focus on ..."
	// This avoids grabbing examples like: For example, "Focus on bees specifically addressing hive-making."
	const extractTopicAndSubtopic = (promptContent) => {
		const text = String(promptContent || '').trim();
		const anchored = text.match(/^"?\s*Focus on\s+([^,.\n\r]+)(?:,?\s*specifically addressing\s+([^.\n\r]+))?\./i);
		if (anchored) {
			const topic = anchored[1]?.trim() || null;
			const sub = anchored[2]?.trim() || null;
			return { rawTopic: topic || null, rawSubtopic: sub || null };
		}
		return { rawTopic: null, rawSubtopic: null };
	};

	// Build a reasonable title from content if no explicit topic is available
	const buildTitleFromContent = (promptContent) => {
		const text = String(promptContent || '').trim();
		// If the prompt starts with Focus on..., prefer that phrasing as title
		const anchored = text.match(/^"?\s*Focus on\s+([^,.\n\r]+)(?:,?\s*specifically addressing\s+([^.\n\r]+))?\./i);
		if (anchored) {
			const topic = (anchored[1] || '').trim();
			const sub = (anchored[2] || '').trim();
			return sanitizeFileName(sub ? `${topic} specifically addressing ${sub}` : topic).slice(0, 160);
		}
		// Otherwise, take the first meaningful line as a short title
		const firstLine = (text.split(/\r?\n/).map(s => s.trim()).find(l => l.length > 0) || '')
			.replace(/^content[:\-\s]+/i, '')
			.replace(/^prompt[:\-\s]+/i, '')
			.trim();
		return sanitizeFileName(firstLine.slice(0, 80) || 'Untitled Prompt');
	};

	// Save the Prompt Input content as a Saved Prompt (same endpoint as Prompt Generator)
	const savePromptInputAsync = async () => {
		const content = (prompt || '').trim();
		if (!content) {
			setToast({ show: true, type: 'warning', message: 'No prompt to save!' });
			return;
		}
		try {
			if (!authService.isAuthenticated()) {
				setToast({ show: true, type: 'error', message: 'Please log in to save prompts' });
				return;
			}
			// Prefer explicit PG fields; otherwise extract only if content begins with the pattern
			let extractedTopic = (pgTopicInput || '').trim() || null;
			let extractedSubtopic = (pgSubtopicInput || '').trim() || null;
			if (!extractedTopic) {
				const { rawTopic, rawSubtopic } = extractTopicAndSubtopic(content);
				extractedTopic = rawTopic || null;
				extractedSubtopic = rawSubtopic || null;
			}
			const categoryId = pgSelectedCategory ? parseInt(pgSelectedCategory) : null;
			const styleTemplateId = pgSelectedStyle ? parseInt(pgSelectedStyle) : null;
			const outcomeTemplateId = pgSelectedOutcomeTemplate ? pgSelectedOutcomeTemplate.id : null;
			const categoryName = categoryId ? (pgCategories.find(c => c.id === categoryId)?.name || '') : '';
			const title = (extractedTopic || '').trim() || buildTitleFromContent(content);
			const promptData = {
				title,
				content,
				description: categoryName ? `Saved from AI Playground (${categoryName})` : 'Saved from AI Playground',
				// Omit isPublic to allow server to apply user default visibility
				categoryId,
				topicId: null,
				subtopicId: null,
				rawTopic: extractedTopic,
				rawSubtopic: extractedSubtopic,
				styleTemplateId,
				outcomeTemplateId,
				aiModelId: null,
				tags: `${categoryName} ${extractedTopic || ''}`.trim()
			};
			const resp = await fetch('/api/savedprompts', {
				method: 'POST',
				headers: {
					...authService.getAuthHeaders(),
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify(promptData)
			});
			if (resp.ok) {
				setToast({ show: true, type: 'success', message: 'Prompt saved successfully!' });
			} else {
                let serverMsg = null;
                try { serverMsg = await resp.json(); } catch { try { serverMsg = await resp.text(); } catch {}
                }
                if (serverMsg && typeof serverMsg === 'object' && serverMsg.code === 'free_saved_limit') {
                    setToast({ show: true, type: 'warning', message: serverMsg.message || 'Free plan limit reached: up to 12 saved prompts.' });
                } else {
                    console.error('Failed to save prompt:', serverMsg || resp.status);
                    setToast({ show: true, type: 'error', message: 'Failed to save prompt. Please try again.' });
                }
			}
		} catch (e) {
			console.error('Error saving prompt:', e);
			setToast({ show: true, type: 'error', message: 'Failed to save prompt. Please try again.' });
		}
	};

	const currentPrice = useMemo(() => {
		const p = (pricing || []).find(p => String(p.provider).toLowerCase() === String(providerKey).toLowerCase() && String(p.model).toLowerCase() === String(model).toLowerCase());
		if (!p) return null;
		const inCentsPer1K = Number(p.inputPricePer1K || 0) * 100;
		const outCentsPer1K = Number(p.outputPricePer1K || 0) * 100;
		return {
			inStr: inCentsPer1K >= 0.1 ? `${inCentsPer1K.toFixed(2)}¢/1K` : `< 0.10¢/1K`,
			outStr: outCentsPer1K >= 0.1 ? `${outCentsPer1K.toFixed(2)}¢/1K` : `< 0.10¢/1K`
		};
	}, [pricing, providerKey, model]);


	const currentProvider = useMemo(() => providers.find(p => p.key === providerKey), [providers, providerKey]);

	useEffect(() => {
        if (currentProvider) {
            // Try server pref model first on provider change; fall back to saved/local
            (async () => {
                try {
                    const t = authService.getToken && authService.getToken();
                    const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
                    let preferred = null;
                    if (res && res.ok) {
                        const prefs = await res.json();
                        preferred = prefs?.DefaultModel || null;
                    }
                    let savedModel = localStorage.getItem('ai_model');
                    if (savedModel && /^sonar/i.test(savedModel)) {
                        savedModel = 'llama-4-scout';
                        try { localStorage.setItem('ai_model', savedModel); } catch {}
                    }
                    const candidate = preferred || savedModel;
                    if (candidate) {
                        setModel(candidate);
                    } else if (String(currentProvider.key).toLowerCase() === 'openai' && (currentProvider.allowed||[]).includes('gpt-4o-mini')) {
                        setModel('gpt-4o-mini');
                    } else {
                        setModel(currentProvider.defaultModel || (currentProvider.allowed||[])[0] || '');
                    }
                } catch {
                    let savedModel = localStorage.getItem('ai_model');
                    if (savedModel && /^sonar/i.test(savedModel)) {
                        savedModel = 'llama-4-scout';
                        try { localStorage.setItem('ai_model', savedModel); } catch {}
                    }
                    if (savedModel) setModel(savedModel); else if (String(currentProvider.key).toLowerCase() === 'openai' && (currentProvider.allowed||[]).includes('gpt-4o-mini')) setModel('gpt-4o-mini'); else setModel(currentProvider.defaultModel || (currentProvider.allowed||[])[0] || '');
                }
            })();
        }
	}, [currentProvider]);

	// Refresh usage chip when window regains focus
	useEffect(() => {
		const onFocus = () => setUsageRefreshTick(t => t + 1);
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, []);

	// Removed dot animation; using CSS loader instead

	const fetchBalanceCents = async () => {
		try {
			const t = authService.getToken && authService.getToken();
			const r = await fetch('/api/billing/balance', { credentials: 'include', headers: t ? { Authorization: `Bearer ${t}` } : {} });
			if (r.ok) {
				const j = await r.json();
				setChatBalanceCents(j?.balanceCents ?? 0);
			}
		} catch {}
	};

	const openChatModal = async () => {
		if (!isPaidUser) { setToast({ show:true, type:'warning', message:'Chat mode is available for Paid plans.'}); return; }
		await fetchBalanceCents();
		setShowChatModal(true);
	};

	const confirmChatMode = () => {
		const pct = Math.max(0, Math.min(90, Number(chatPct || 0)));
		const bal = Number(chatBalanceCents || 0);
		const cco = Math.floor(bal * (1 - (pct / 100)));
		const contextPairs = Math.max(5, Math.min(30, Number(chatContextWindow || 15)));
		setChatMode(true);
		setChatCcoCents(cco);
		setChatContextWindow(contextPairs);
		localStorage.setItem('ai_chatMode', '1');
		localStorage.setItem('ai_chatPct', String(pct));
		localStorage.setItem('ai_chatCcoCents', String(cco));
		localStorage.setItem('ai_chatContextWindow', String(contextPairs));
		setShowChatModal(false);
	};
	// Preflight estimate for Chat mode
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			if (!chatMode) { setEstCostCents(null); return; }
			const currentPrompt = (prompt || '').trim();
			if (!currentPrompt) { setEstCostCents(null); return; }
			try {
				setEstLoading(true);
				const history = (thread || []).map(m => ({ role: m.role || 'user', content: m.text || '' }));
				// Use user default max tokens if available
				let defMax = 4096;
				try { const v = localStorage.getItem('ai_defaultMaxTokens'); if (v) defMax = parseInt(v,10) || defMax; } catch {}
				const body = { provider: providerKey, model, prompt: currentPrompt, history, maxOutputTokens: defMax };
				const t = authService.getToken && authService.getToken();
				const r = await fetch('/api/ai/preflight-cost', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body), credentials:'include' });
				if (!cancelled && r.ok) {
					const j = await r.json();
					setEstCostCents(Number(j?.estCostCents || 0));
				}
			} catch {
				if (!cancelled) setEstCostCents(null);
			} finally {
				if (!cancelled) setEstLoading(false);
			}
		};
		const id = setTimeout(run, 400);
		return () => { cancelled = true; clearTimeout(id); };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatMode, prompt, providerKey, model, thread]);


	const exitChatMode = () => {
		setChatMode(false);
		setTempContextExpansion(0); // Reset temporary expansion when exiting chat mode
		setArchivedCount(0); // Clear archived count when exiting chat mode
		localStorage.setItem('ai_chatMode', '0');
	};

  const [doNotStore, setDoNotStore] = useState(false);
  const [sendRedactPii, setSendRedactPii] = useState(false);
  const [showCostDetails, setShowCostDetails] = useState(false);
  const [capCents, setCapCents] = useState(null);
  const [stopAtCap, setStopAtCap] = useState(true);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [sessionRequests2, setSessionRequests2] = useState(0);
  const [sessionInTokens2, setSessionInTokens2] = useState(0);
  const [sessionOutTokens2, setSessionOutTokens2] = useState(0);
  const [sessionCostCents2, setSessionCostCents2] = useState(0);
  const [lastRequestId, setLastRequestId] = useState(null);
  const [showPii, setShowPii] = useState(false);
  const [piiFindings, setPiiFindings] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [piiPending, setPiiPending] = useState(null); // Set but not currently read (used for state tracking)
  const [piiBypass, setPiiBypass] = useState(false);

  const findPii = (text) => {
    const matches = new Set();
    try {
      if (!text) return [];
      const email = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/ig;
      const phone = /(?:\+?\d{1,3}[\s\-.])?(?:\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4})/g;
      const ssn = /\b\d{3}-\d{2}-\d{4}\b/g;
      let m;
      while ((m = email.exec(text)) && matches.size < 10) matches.add(m[0]);
      while ((m = phone.exec(text)) && matches.size < 10) matches.add(m[0]);
      while ((m = ssn.exec(text)) && matches.size < 10) matches.add(m[0]);
    } catch {}
    return Array.from(matches);
  };

  const redactText = (text, findings) => {
    if (!text || !findings || findings.length === 0) return text;
    let t = String(text);
    for (const f of findings) {
      try {
        const re = new RegExp(f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        t = t.replace(re, '[redacted]');
      } catch {}
    }
    return t;
  };

  const [showModeration, setShowModeration] = useState(false);
  const [modCategories, setModCategories] = useState([]);
  const [modDecision, setModDecision] = useState('allow');
  const [modMessage, setModMessage] = useState('');
  const [modBypassOnce, setModBypassOnce] = useState(false);
  const [modStatus, setModStatus] = useState(null); // { decision, source, categories }
  const piiDialogRef = React.useRef(null);
  const modDialogRef = React.useRef(null);

  // Focus trap + ESC for PII modal
  React.useEffect(() => {
    if (!showPii) return;
    const container = piiDialogRef.current;
    if (!container) return;
    const focusSelectors = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusFirst = () => {
      const nodes = container.querySelectorAll(focusSelectors);
      if (nodes && nodes.length > 0) {
        nodes[0].focus();
      } else {
        container.focus();
      }
    };
    const onKeyDown = (e) => {
      if (!piiDialogRef.current) return;
      const nodes = piiDialogRef.current.querySelectorAll(focusSelectors);
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowPii(false); setPiiPending(null);
      } else if (e.key === 'Tab') {
        if (!nodes || nodes.length === 0) { e.preventDefault(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first || !piiDialogRef.current.contains(document.activeElement)) {
            last.focus(); e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus(); e.preventDefault();
          }
        }
      }
    };
    const t = setTimeout(focusFirst, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKeyDown); };
  }, [showPii]);

  // Focus trap + ESC for Moderation modal
  React.useEffect(() => {
    if (!showModeration) return;
    const container = modDialogRef.current;
    if (!container) return;
    const focusSelectors = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusFirst = () => {
      const nodes = container.querySelectorAll(focusSelectors);
      if (nodes && nodes.length > 0) nodes[0].focus(); else container.focus();
    };
    const onKeyDown = (e) => {
      if (!modDialogRef.current) return;
      const nodes = modDialogRef.current.querySelectorAll(focusSelectors);
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowModeration(false); setPending(false);
      } else if (e.key === 'Tab') {
        if (!nodes || nodes.length === 0) { e.preventDefault(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first || !modDialogRef.current.contains(document.activeElement)) {
            last.focus(); e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus(); e.preventDefault();
          }
        }
      }
    };
    const t = setTimeout(focusFirst, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKeyDown); };
  }, [showModeration]);

  const onSubmit = async (skipPii = false, override = null) => {
    const useProvider = (override && override.provider) ? override.provider : providerKey;
    const useModel = (override && override.model) ? override.model : model;
    const bypassNow = !!skipPii;
    if (piiBypass) { setPiiBypass(false); }
		setError('');
		setPending(true);
		setUsage(null);
		const currentPrompt = (prompt || '').trim();
		if (!currentPrompt) { setPending(false); return; }
    // Moderation check unless bypassed via explicit ack
    if (!modBypassOnce) {
      try {
        const token = authService.getToken && authService.getToken();
        const attachTextForModeration = (attachments||[])
          .filter(a => {
            try {
              const mt = String((a && (a.MimeType || a.mimeType || a.type)) || '').toLowerCase();
              return !(mt.startsWith('image/') || mt.startsWith('video/') || mt === 'application/zip');
            } catch { return true; }
          })
          .map(a => a?.text || '');
        const r = await fetch('/api/ai/moderate', { method:'POST', headers:{ 'Content-Type':'application/json', ...(token?{ Authorization: `Bearer ${token}` }: {}) }, body: JSON.stringify({ text: [currentPrompt, ...attachTextForModeration].join('\n\n') }), credentials:'include' });
        if (r.ok) {
          const j = await r.json();
          try { setModStatus({ decision: String(j.decision||'allow'), source: j.source || null, categories: Array.isArray(j.categories) ? j.categories : [] }); } catch {}
          if (j && (j.decision === 'warn' || j.decision === 'block')) {
            setPending(false);
            setShowModeration(true);
            setModDecision(j.decision || 'warn');
            setModCategories(Array.isArray(j.categories) ? j.categories : []);
            setModMessage((j.source ? (j.source+': ') : '') + (j.message || 'Potential policy issues detected'));
            return;
          }
        }
      } catch {}
    } else {
      setModBypassOnce(false);
    }
    if (!bypassNow) {
      try {
        const attachTextForLocalPii = (attachments||[])
          .filter(a => {
            try {
              const mt = String((a && (a.MimeType || a.mimeType || a.type)) || '').toLowerCase();
              return !(mt.startsWith('image/') || mt.startsWith('video/') || mt === 'application/zip');
            } catch { return true; }
          })
          .map(a => a?.text || '');
        const combined = [currentPrompt, ...attachTextForLocalPii].join('\n');
        const found = findPii(combined);
        if (found && found.length > 0) {
          setPending(false);
          setPiiFindings(found.slice(0, 10));
          setShowPii(true);
          setPiiPending({ prompt: currentPrompt });
          return;
        }
      } catch {}
    }
		// Preflight in Chat mode (PaidUser-only endpoint protection exists server-side)
    if (chatMode) {
			try {
				await fetchBalanceCents();
				// Calculate context limit (user setting + temporary expansion)
				// Note: currentPrompt will be added to thread, so we use thread as-is for preflight
				const contextPairs = Number(chatContextWindow || 15);
				const contextLimit = (contextPairs * 2) + tempContextExpansion; // pairs * 2 = messages, plus any temp expansion
				// Include current prompt in the count, so slice based on remaining messages
				const messagesToInclude = Math.max(1, contextLimit - 1); // -1 to account for current prompt
				const recentMessages = (thread || []).slice(-messagesToInclude);
				const history = recentMessages.map(m => ({ role: m.role || 'user', content: m.text || '' }));
				let defMax2 = 4096; try { const v = localStorage.getItem('ai_defaultMaxTokens'); if (v) defMax2 = parseInt(v,10) || defMax2; } catch {}
        const body = { provider: useProvider, model: useModel, prompt: currentPrompt, history, maxOutputTokens: defMax2 };
				const t = authService.getToken && authService.getToken();
				const r = await fetch('/api/ai/preflight-cost', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body), credentials: 'include' });
				if (r.ok) {
					const j = await r.json();
					const est = Number(j?.estCostCents || 0);
					const bal = Number(chatBalanceCents || 0);
					const cco = Number(chatCcoCents || 0);
					const warnPct = 10; // client default; server enforces separately
					const epsilonPct = 2; // mirror server epsilon for UX
					const minOperating = 50; // cents
					const predictedRemaining = bal - est;
					const requiredFloor = Math.max(Math.ceil(cco * (1 + epsilonPct / 100)), minOperating);
					if (predictedRemaining < requiredFloor) {
						setPending(false);
						setToast({ show: true, type: 'warning', message: `This send would breach your CCO ($${(cco/100).toFixed(2)}). Edit CCO or reduce prompt.` });
						return;
					}
					const warnFloor = Math.ceil(cco * (1 + warnPct / 100));
					if (predictedRemaining <= warnFloor) {
						setToast({ show: true, type: 'warning', message: `Near CCO warning: remaining would be close to $${(cco/100).toFixed(2)}.` });
					}
					if (bal > 0 && est > bal) { setPending(false); setToast({ show: true, type: 'warning', message: 'Insufficient balance for chat.' }); return; }
				}
			} catch {}
		}
		// Update thread with new user message first, capturing the new thread snapshot for this send
    let newThreadSnapshot = null;
		setThread(prev => {
      const next = [{ role: 'user', text: currentPrompt, label: userFirstName || 'You' }, ...(prev || [])];
      newThreadSnapshot = next;
      return next;
    });
		setIoText(prev => {
      const block = `${userFirstName || 'You'}:\n${currentPrompt}`;
      return prev ? `${prev}\n\n${block}` : block;
    });
		let hadImage = false;
		try {
			try { hadImage = !!((attachments||[]).some(a => a && a.Base64)); } catch {}
			const token = authService.getToken();
      // Calculate context limit (user setting + temporary expansion for "load more")
			const contextPairs = Number(chatContextWindow || 15);
			const contextLimit = (contextPairs * 2) + tempContextExpansion; // pairs * 2 = messages, plus any temp expansion
			let history = undefined;
			let calcArchivedCount = 0;
			const threadForHistory = newThreadSnapshot || thread || [];
			if (chatMode && threadForHistory.length > 0) {
				const recentMessages = threadForHistory.slice(-contextLimit);
				calcArchivedCount = Math.max(0, threadForHistory.length - contextLimit);
				history = recentMessages.map(m => ({ role: m.role || 'user', content: m.text || '' }));
				// Update archived count state for UI display
				setArchivedCount(calcArchivedCount);
				// Reset temporary expansion after using it
				if (tempContextExpansion > 0) {
					setTempContextExpansion(0);
				}
			} else {
				setArchivedCount(0);
			}
			let defMax3 = 4096; try { const v = localStorage.getItem('ai_defaultMaxTokens'); if (v) defMax3 = parseInt(v,10) || defMax3; } catch {}
      const aMime = (a) => (a && (a.MimeType || a.mimeType || a.type)) || '';
      const defaultTemp = 0.7;
      const isDefaultTemp = Math.abs(temperature - defaultTemp) < 0.01;
      const isDefaultTopP = Math.abs(topP - 0.9) < 0.01;
      const basePayload = { 
        provider: useProvider, 
        model: useModel, 
        prompt: currentPrompt, 
        maxOutputTokens: defMax3, 
        doNotStoreContent: !!doNotStore, 
        redactPii: !!sendRedactPii, 
        ocrIfEmpty: !!ocrIfEmpty, 
        ocrLangs: (ocrLangs||'').trim() || null, 
        attachments: (attachments||[]).map(a => ({ name: a.name, type: a.type, size: a.size, text: a.text, base64: a.Base64, mimeType: aMime(a), include: a.include !== false, useExtract: a.useExtract, useVision: a.useVision, pageStart: a.pageStart, pageEnd: a.pageEnd })),
        ...(isStudioMode && !isDefaultTemp ? { temperature } : {}),
        ...(isStudioMode && !isDefaultTopP ? { topP } : {})
      };
      const bodyComplete = history ? { ...basePayload, history, chatMode: true, chatCcoCents: chatCcoCents } : basePayload;
      // One-shot flag; reset after building payload
      if (sendRedactPii) setSendRedactPii(false);
			const idemKey = (window.crypto?.randomUUID ? window.crypto.randomUUID() : (Date.now().toString(36) + Math.random().toString(36).slice(2)));
            if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch {} }
            abortControllerRef.current = new AbortController();
            const resp = await fetch('/api/ai/complete', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { 'Authorization': `Bearer ${token}` } : {}),
					'X-Idempotency-Key': idemKey
				},
                body: JSON.stringify(bodyComplete),
                signal: abortControllerRef.current.signal
			});
			if (resp.status === 429) {
				setPending(false);
				const ra = resp.headers.get('Retry-After');
				const secs = Math.max(1, parseInt(ra||'5', 10) || 5);
				setToast({ show:true, type:'warning', message:`Rate limit reached. Try again in ${secs}s.` });
				return;
			}
			if (resp.status === 403) throw new Error('Access denied. PaidUser role required.');
            if (resp.status === 400 || resp.status === 402) {
                try {
                    const j = await resp.json();
                    if (j && j.code === 'attachment_invalid') {
                        setToast({ show: true, type: 'warning', message: j.message || 'Attachment rejected. Please check file type/size.' });
                    } else if (j && j.code === 'free_daily_limit') {
                        setToast({ show: true, type: 'warning', message: j.message || 'You have used your daily allowance of Free AI requests. Become a Pro User for as little as $5.' });
                    } else if (j && j.code === 'free_model_restricted') {
                        setToast({ show: true, type: 'warning', message: j.message || 'This model is available on paid plans. Please select GPT-4o-mini or Gemini 2.5 Flash.' });
                    } else if (j && j.code === 'insufficient_funds') {
                        const est = typeof j?.estCostCents === 'number' ? `$${(j.estCostCents/100).toFixed(2)}` : 'unknown';
                        const bal = typeof j?.balanceCents === 'number' ? `$${(j.balanceCents/100).toFixed(2)}` : '—';
                        const cap = typeof j?.maxRequestCostCents === 'number' && j.maxRequestCostCents>0 ? `$${(j.maxRequestCostCents/100).toFixed(2)}` : null;
                        setToast({ show: true, type: 'warning', message: `Estimated cost ${est} exceeds ${cap ? `cap ${cap} or ` : ''}balance (${bal}).` });
                    } else if (j && j.code === 'exceeds_cco') {
                        const est = typeof j?.estCostCents === 'number' ? `$${(j.estCostCents/100).toFixed(2)}` : 'unknown';
                        const bal = typeof j?.balanceCents === 'number' ? `$${(j.balanceCents/100).toFixed(2)}` : '—';
                        const cco = typeof j?.chatCcoCents === 'number' && j.chatCcoCents>0 ? `$${(j.chatCcoCents/100).toFixed(2)}` : null;
                        setToast({ show: true, type: 'warning', message: `Chat budget floor (CCO) would be breached: est ${est}, balance ${bal}${cco ? `, CCO ${cco}` : ''}.` });
                    } else {
                        const serverMsg = (j && typeof j.message === 'string' && j.message.trim().length > 0) ? j.message : null;
                        // Check if error is related to size (image/request too large)
                        const isSizeError = serverMsg && (
                            /too large/i.test(serverMsg) || 
                            /exceeds.*size/i.test(serverMsg) || 
                            /request.*too large/i.test(serverMsg) || 
                            /image.*too large/i.test(serverMsg) ||
                            /file.*too large/i.test(serverMsg) ||
                            /maximum.*size/i.test(serverMsg) ||
                            /size.*limit/i.test(serverMsg)
                        );
                        if (isSizeError) {
                            setToast({ show: true, type: 'warning', message: 'Image/Message too large.' });
                        } else if (serverMsg) {
                            setToast({ show: true, type: 'warning', message: serverMsg });
                        } else {
                            setToast({ show: true, type: 'warning', message: 'Request could not be processed.' });
                        }
                    }
                } catch (e) {
                    // If JSON parsing fails, try to read as text to check for size-related errors
                    try {
                        const text = await resp.text();
                        const isSizeError = text && (
                            /too large/i.test(text) || 
                            /exceeds.*size/i.test(text) || 
                            /request.*too large/i.test(text) || 
                            /image.*too large/i.test(text) ||
                            /file.*too large/i.test(text) ||
                            /maximum.*size/i.test(text) ||
                            /size.*limit/i.test(text) ||
                            /400.*bad request/i.test(text)
                        );
                        if (isSizeError) {
                            setToast({ show: true, type: 'warning', message: 'Image/Message too large.' });
                        } else {
                            setToast({ show: true, type: 'warning', message: 'Request could not be processed.' });
                        }
                    } catch {
                        setToast({ show: true, type: 'warning', message: 'Request could not be processed.' });
                    }
                }
                setPending(false);
                return;
            }
			if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
	      const isReplay = (resp.headers.get('X-Idempotent-Replayed') === 'true');
	      const data = await resp.json();
      const provLabel = (data && (data.provider || data.Provider)) || useProvider || providerKey;
      const modelLabel = (data && (data.model || data.Model)) || useModel || model;
      const formatted = `${provLabel}${modelLabel ? ` (${modelLabel})` : ''}:\n${data.text || ''}`;
			setOutput(prev => {
        if (!prev) return formatted;
        return `${prev}\n\n${formatted}`;
      });
			setUsage(data.usage || null);
      try { setLastRequestId(data?.requestId || null); } catch {}
	      try {
	        const inTok = Number(data?.usage?.inputTokens||0);
	        const outTok = Number(data?.usage?.outputTokens||0);
	        const costUsd = Number(data?.usage?.costUsd||0);
	        if (!isReplay) {
	          setSessionRequests2(v=>v+1);
	          setSessionInTokens2(v=>v+inTok);
	          setSessionOutTokens2(v=>v+outTok);
	          if (!Number.isNaN(costUsd) && costUsd>0) setSessionCostCents2(v=>v+Math.round(costUsd*100));
	        }
	      } catch {}
      const aiEntry = { role: 'ai', text: data.text || '', label: `${provLabel}${modelLabel ? ` (${modelLabel})` : ''}` };
			setThread(prev => {
        const arr = [...(prev || [])];
        // Insert AI response right after the most recent user message (index 1 if user was just added)
        const insertAt = Math.min(1, arr.length);
        arr.splice(insertAt, 0, aiEntry);
        return arr;
			});
			setIoText(prev => {
        const formatted = `${provLabel}${modelLabel ? ` (${modelLabel})` : ''}:\n${data.text || ''}`;
        return (prev ? `${prev}\n\n${formatted}` : formatted);
      });
			const inTok = data?.usage?.inputTokens || data?.usage?.InputTokens || 0;
			const outTok = data?.usage?.outputTokens || data?.usage?.OutputTokens || 0;
			if (!isReplay) {
				setSessionRequests(x => x + 1);
				setSessionTokensIn(x => x + inTok);
				setSessionTokensOut(x => x + outTok);
			}
			// If backend provides an estimated cost in USD, track it per request and for the session; otherwise estimate from pricing
			let costUsd = (data?.usage && (data.usage.costUsd ?? data.usage.totalCostUsd ?? null));
			if (!(typeof costUsd === 'number' && Number.isFinite(costUsd))) {
				const p = (pricing || []).find(p => (p.provider||'').toLowerCase() === (providerKey||'').toLowerCase() && (p.model||'').toLowerCase() === (model||'').toLowerCase());
				if (p) {
					const inCost = (p.inputPricePer1K || 0) * (inTok / 1000);
					const outCost = (p.outputPricePer1K || 0) * (outTok / 1000);
					costUsd = Number(inCost + outCost);
				}
			}
			if (typeof costUsd === 'number' && Number.isFinite(costUsd)) {
				setLastRequestCostUsd(costUsd);
				if (!isReplay) setSessionTotalCostUsd(total => total + costUsd);
				// Chat mode post-send: fetch new balance and compare to CCO for warn/exit
				if (chatMode && !isReplay) {
					try {
						const t = authService.getToken && authService.getToken();
						const rb = await fetch('/api/billing/balance', { credentials: 'include', cache: 'no-store', headers: t ? { Authorization: `Bearer ${t}` } : {} });
						if (rb.ok) {
							const bj = await rb.json();
							const newBal = Number(bj?.balanceCents || 0);
							setChatBalanceCents(newBal);
							const cco = Number(chatCcoCents || 0);
							if (cco > 0) {
								if (newBal <= cco) {
									setToast({ show: true, type: 'warning', message: 'Reached Chat cutoff. Switching to Work mode.' });
									exitChatMode();
								} else if (newBal <= Math.ceil(cco * 1.10)) {
									setToast({ show: true, type: 'warning', message: 'Remaining balance is within 10% of your CCO.' });
								}
							}
						}
					} catch {}
				}
			} else {
				setLastRequestCostUsd(null);
			}
			if (!isReplay && tokensRemaining != null && Number.isFinite(+tokensRemaining)) {
				const next = (+tokensRemaining) - (inTok + outTok);
				setTokensRemaining(next < 0 ? 0 : next);
			}
        } catch (e) {
			setError(e.message || 'Request failed');
            if (e && (e.name === 'AbortError' || e.code === 20)) {
                setError('Cancelled by user');
                try { setToast({ show:true, type:'warning', message:'Request cancelled.' }); } catch {}
                return;
            }
			// If sending an image failed, show a support toast per requirements
			try {
				if (hadImage) {
					setToast({ show: true, type: 'error', message: 'Image send failed. If this happens repeatedly, contact support@pocketpromptengineer.com' });
				}
			} catch {}
        } finally {
			setPending(false);
            if (abortControllerRef.current) { abortControllerRef.current = null; }
			// Re-enable Optimize after a real send
			setOptimizeLocked(false);
			// Refresh usage chip after each send
			setUsageRefreshTick(t => t + 1);
		}
	};

    const cancelInFlight = () => {
        try {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        } catch {}
    };

	const optimizePrompt = async () => {
		if (optimizing) return;
		const currentPrompt = (prompt || '').trim();
		if (currentPrompt.length <= 30) return;
		setOptimizing(true);
		setError('');
		try {
			const token = authService.getToken();
			const instruction = 'Optimize this prompt to the form: "[Style Template]. Focus on [Topic] specifically addressing [Subtopic]. [Outcome Template]."  Return only the prompt in the form of 3 sentences.';
			const body = {
				provider: 'OpenAI',
				model: null, // use default OpenAI model on server
				prompt: instruction + '\n\n' + currentPrompt,
				maxOutputTokens: 4096
			};
			const resp = await fetch('/api/ai/complete', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
				body: JSON.stringify(body)
			});
			if (resp.status === 429) {
				const ra = resp.headers.get('Retry-After');
				const secs = Math.max(1, parseInt(ra||'5', 10) || 5);
				setToast({ show:true, type:'warning', message:`Rate limit reached for optimize. Try again in ${secs}s.` });
				return;
			}
			if (resp.status === 403) {
				setToast({ show: true, type: 'error', message: 'Optimize is available for signed-in users with access.' });
				return;
			}
			if (!resp.ok) {
				const text = await resp.text();
				throw new Error(text || `Optimize failed (${resp.status})`);
			}
			const data = await resp.json();
			const newPrompt = (data && (data.text || data.output || '')) || '';
			// Replace the PI content only; do not post into AIO thread
			setPrompt('');
			setTimeout(() => setPrompt(newPrompt), 0);
			setToast({ show: true, type: 'success', message: 'Prompt optimized.' });
			// Lock Optimize until user sends the prompt
			setOptimizeLocked(true);
			// NOTE: Cost/usage should be counted toward PaidUser or FreeUser daily quota in backend.
		} catch (e) {
			setToast({ show: true, type: 'error', message: e.message || 'Optimize failed.' });
		} finally {
			setOptimizing(false);
		}
	};

	const sanitizeFileName = (name) => {
		return (name || '').replace(/[^a-zA-Z0-9 _\-().]/g, '').trim().slice(0, 160) || `ai-output-${new Date().toISOString().replace(/[:.]/g,'-')}`;
	};

	const performSaveAsync = async (title, skipWarning = false) => {
		// Check save count limits
		// Use tier info for save limits
		const { tier, maxSavedConversations, savedConversationCount } = tierInfo;
		const isTeamOrEnterprise = tier === 'Team' || tier === 'Enterprise';
		
		// Check save limit
		if (maxSavedConversations !== null && savedConversationCount >= maxSavedConversations) {
			const upgradeMsg = tier === 'Free' 
				? 'Upgrade to Pro for 100 saved conversations.'
				: 'Upgrade to Teams or Enterprise for unlimited conversation storage.';
			setToast({ 
				show: true, 
				type: 'warning', 
				message: `You've reached the ${tier} tier limit of ${maxSavedConversations} saved conversations. ${upgradeMsg}` 
			});
			return;
		}
		
		// Determine what to save based on tier
		let threadToSave = thread || [];
		const needsWarning = !skipWarning && !isTeamOrEnterprise && archivedCount > 0;
		
		if (needsWarning) {
			// Show warning modal
			setPendingSaveTitle(title);
			setShowSaveWarningModal(true);
			return;
		}
		
		// Team/Enterprise: Save full conversation
		// Free/Pro: Truncate to last 15 pairs (30 messages)
		if (!isTeamOrEnterprise && threadToSave.length > 30) {
			threadToSave = threadToSave.slice(-30);
		}
		
		const token = authService.getToken();
		const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
		const body = {
			Id: null,
			Title: sanitizeFileName(title),
			Provider: providerKey,
			Model: model,
			IoText: ioText || output || '',
			PromptText: prompt || '',
			ThreadJson: JSON.stringify(threadToSave),
			SessionRequests: sessionRequests,
			SessionTokensIn: sessionTokensIn,
			SessionTokensOut: sessionTokensOut,
			SessionTotalCostUsd: typeof sessionTotalCostUsd === 'number' ? Number(sessionTotalCostUsd.toFixed(6)) : null
		};
		const resp = await fetch('/api/aiiosaves', { method: 'POST', headers, body: JSON.stringify(body), credentials: 'include' });
		if (!resp.ok) {
			const errorData = await resp.json().catch(() => null);
			if (errorData && errorData.code === 'save_limit_reached') {
				const tierMsg = tierInfo.tier === 'Free' 
					? "You've reached the Free tier limit of 10 saved conversations. Upgrade to Pro for 100 saved conversations, or Teams/Enterprise for unlimited saves."
					: "You've reached the Pro tier limit of 100 saved conversations. Upgrade to Teams or Enterprise for unlimited conversation storage.";
				setToast({ show: true, type: 'warning', message: tierMsg });
				// Refresh tier info to get updated counts
				refreshTier();
			} else {
				throw new Error(`Save failed (${resp.status})`);
			}
			return;
		}
		await refreshSavedList();
		// Refresh tier info to update saved conversation count
		refreshTier();
		setToast({ show: true, type: 'success', message: 'Conversation saved.' });
	};

	const loadSavedAsync = async (id) => {
		try {
			const token = authService.getToken();
			const headers = token ? { Authorization: `Bearer ${token}` } : {};
			const resp = await fetch(`/api/aiiosaves/${id}`, { headers, credentials: 'include' });
			if (!resp.ok) throw new Error(`Load failed (${resp.status})`);
			const item = await resp.json();
			setProviderKey(item.provider || providerKey);
			setModel(item.model || '');
			setPrompt(item.promptText || '');
			setIoText(item.ioText || '');
			try {
				const parsed = item.threadJson ? JSON.parse(item.threadJson) : [];
				setThread(Array.isArray(parsed) ? parsed : []);
			} catch { setThread([]); }
			setSessionRequests(item.sessionRequests || 0);
			setSessionTokensIn(item.sessionTokensIn || 0);
			setSessionTokensOut(item.sessionTokensOut || 0);
			setSessionTotalCostUsd(typeof item.sessionTotalCostUsd === 'number' ? item.sessionTotalCostUsd : 0);
			setShowRecall(false);
			setToast({ show: true, type: 'success', message: 'Session loaded.' });
		} catch (e) {
			setToast({ show: true, type: 'error', message: e.message || 'Failed to load.' });
		}
	};

	const renameSaveAsync = async (id, name) => {
		const newName = sanitizeFileName(name || '');
		if (!newName) { setToast({ show:true, type:'warning', message:'Please enter a name.' }); return; }
		try {
			const token = authService.getToken();
			const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
			// Backend expects PUT /api/aiiosaves/{id}/rename with a JSON string body
			const resp = await fetch(`/api/aiiosaves/${id}/rename`, { method: 'PUT', headers, body: JSON.stringify(newName), credentials: 'include' });
			if (!resp.ok) throw new Error(`Rename failed (${resp.status})`);
			await refreshSavedList();
			setToast({ show: true, type: 'success', message: 'Renamed.' });
			setShowRenameModal(false);
			setRenameId(null);
			setRenameTitle('');
		} catch (e) {
			setToast({ show: true, type: 'error', message: e.message || 'Failed to rename.' });
		}
	};

	const deleteSaveAsync = async (id) => {
		try {
			const token = authService.getToken();
			const headers = token ? { Authorization: `Bearer ${token}` } : {};
			const resp = await fetch(`/api/aiiosaves/${id}`, { method: 'DELETE', headers, credentials: 'include' });
			if (!resp.ok) throw new Error(`Delete failed (${resp.status})`);
			await refreshSavedList();
			setToast({ show: true, type: 'success', message: 'Deleted.' });
		} catch (e) {
			setToast({ show: true, type: 'error', message: e.message || 'Failed to delete.' });
		}
	};

	// Extract file processing logic for reuse by both click and drag-and-drop
	const processFiles = async (files) => {
		if (!files || files.length === 0) return;
		const list = [];
		for (const f of files) {
			// Client-side size gate (PDF 25MB, others 20MB)
			try {
				const isPdfName = /\.pdf$/i.test(f.name);
				const maxBytes = isPdfName ? (25 * 1024 * 1024) : (20 * 1024 * 1024);
				if (typeof f.size === 'number' && f.size > maxBytes) {
					setToast && setToast({ show:true, type:'warning', message:`File too large: ${f.name} exceeds ${Math.floor(maxBytes/(1024*1024))} MB.` });
					continue;
				}
			} catch {}
			const isDocx = /\.docx$/i.test(f.name);
			const isXlsx = /\.(xlsx|xls)$/i.test(f.name);
			const isImage = /^image\//i.test(f.type) || /\.(png|jpe?g|gif|bmp|webp|tif?f|heic|heif)$/i.test(f.name);
			const isPdf = /\.pdf$/i.test(f.name) || /^application\/pdf$/i.test(f.type);
			const isZip = /\.zip$/i.test(f.name) || /(^application\/(zip|x-zip-compressed)$)/i.test(f.type || '') || /zip/i.test(f.type || '');
			const isVideo = /^video\//i.test(f.type) || /\.(mp4|mov|webm|mkv|avi)$/i.test(f.name);
			const isMarkdown = /\.(md|markdown)$/i.test(f.name) || /^text\/markdown$/i.test(f.type) || /markdown/i.test(f.type || '');
			const isRtf = /\.rtf$/i.test(f.name) || /rtf/i.test(f.type || '');
			const isPpt = /\.(pptx?|potx?)$/i.test(f.name) || /presentation/i.test(f.type || '') || /powerpoint/i.test(f.type || '');
			const isAudio = /^audio\//i.test(f.type) || /\.(mp3|wav|m4a|ogg|flac|aac)$/i.test(f.name);
			if (isDocx) {
				const r = new FileReader();
				const buf = await new Promise(resolve => { r.onload = () => resolve(r.result); r.readAsArrayBuffer(f); });
				try {
					const mammoth = await import('mammoth');
					const result = await mammoth.extractRawText({ arrayBuffer: buf });
					list.push({ name: f.name, size: f.size, type: f.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', text: String(result?.value || '') });
				} catch {
					list.push({ name: f.name, size: f.size, type: f.type, text: '' });
				}
			} else if (isXlsx) {
				const r = new FileReader();
				const buf = await new Promise(resolve => { r.onload = () => resolve(r.result); r.readAsArrayBuffer(f); });
				try {
					const XLSX = await import('xlsx');
					const wb = XLSX.read(buf, { type: 'array' });
					let text = '';
					(wb.SheetNames||[]).forEach((sn, idx) => {
						try {
							const ws = wb.Sheets[sn];
							const csv = XLSX.utils.sheet_to_csv(ws) || '';
							text += `\n\n[Sheet ${idx+1}: ${sn}]\n${csv}`;
						} catch {}
					});
					list.push({ name: f.name, size: f.size, type: f.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', text });
				} catch {
					list.push({ name: f.name, size: f.size, type: f.type, text: '' });
				}
			} else if (isPdf) {
				const meta = `PDF file attached: size ${f.size} bytes.`;
				let pdfMeta = null;
				let extractedText = '';
				let renderedImages = [];
				try {
					// Robust import across bundlers (module or default)
					const mod = await import('pdfjs-dist/legacy/build/pdf');
					const pdfjs = (mod && (mod.default && mod.default.getDocument ? mod.default : mod));
					try { pdfjs.GlobalWorkerOptions.workerSrc = undefined; } catch {}
					const r2 = new FileReader();
					const buf = await new Promise(resolve => { r2.onload = () => resolve(r2.result); r2.readAsArrayBuffer(f); });
					const loadingTask = pdfjs.getDocument({ data: new Uint8Array(buf), disableWorker: true });
					let pdf = await loadingTask.promise;
					let pages = Number((pdf && pdf.numPages) || 0) || 0;
					if (!pages || pages <= 0) {
						// Second chance: use a Blob URL (some PDFs load better by URL)
						try {
							const blobUrl = URL.createObjectURL(new Blob([buf], { type: f.type || 'application/pdf' }));
							const task2 = pdfjs.getDocument({ url: blobUrl, disableWorker: true });
							pdf = await task2.promise;
							pages = Number((pdf && pdf.numPages) || 0) || 0;
							URL.revokeObjectURL(blobUrl);
						} catch {}
					}
					if (!pages || pages <= 0) {
						// attach just metadata below (no UI toast; server will handle extraction/fallback)
						pdfMeta = { pageCount: 0, textChars: 0, imageCount: 0 };
					} else {
						let textChars = 0;
						let imageCount = 0;
						const maxPages = Math.min(pages, 20);
						// Pre-resolve OPS
						const OPS = (pdfjs && pdfjs.OPS) ? pdfjs.OPS : {};
						for (let p = 1; p <= maxPages; p++) {
							try {
								const page = await pdf.getPage(p);
								try {
									const tc = await page.getTextContent();
									const lines = (tc.items || []).map(it => it.str || '').filter(Boolean);
									const pageText = lines.join(' ').trim();
									if (pageText) {
										extractedText += `\n\n[Page ${p}]\n${pageText}`;
										textChars += pageText.length;
									}
								} catch {}
								try {
									const ops = await page.getOperatorList();
									const fnArray = ops.fnArray || [];
									const argsArray = ops.argsArray || [];
									for (let i = 0; i < fnArray.length; i++) {
										const fn = fnArray[i];
										const args = argsArray[i];
										if (fn === OPS.paintImageXObject || fn === OPS.paintInlineImageXObject) {
											imageCount++;
										} else if (fn === OPS.paintXObject && Array.isArray(args) && typeof args[0] === 'string' && /Img|Image/i.test(String(args[0]||''))) {
											imageCount++;
										}
									}
								} catch {}
							} catch {}
						}
						pdfMeta = { pageCount: pages, textChars, imageCount };
						// Fallback: if no text extracted but images exist, render first few pages to PNG
						if ((!extractedText || extractedText.trim().length === 0) && imageCount > 0) {
							const renderPages = Math.min(pages, 3);
							for (let p = 1; p <= renderPages; p++) {
								try {
									const page = await pdf.getPage(p);
									const viewport = page.getViewport({ scale: 1.5 });
									const canvas = document.createElement('canvas');
									const ctx = canvas.getContext('2d');
									canvas.width = Math.floor(viewport.width);
									canvas.height = Math.floor(viewport.height);
									await page.render({ canvasContext: ctx, viewport }).promise;
									const dataUrl = canvas.toDataURL('image/png');
									const m = dataUrl.match(/^data:image\/png;base64,(.+)$/);
									if (m) renderedImages.push({ page: p, base64: m[1], mime: 'image/png' });
								} catch {}
							}
						}
					}
				} catch (e) {
					// Swallow client-side PDF probe errors; rely on server extraction/vision fallback
					try { console.warn('PDF probe failed (client). Falling back to server.', e); } catch {}
				}
				const finalText = (extractedText && extractedText.trim()) ? extractedText.trim() : meta;
				// Read Base64 so server can fallback-extract if needed
				let pdfBase64 = '';
				let pdfMime = f.type || 'application/pdf';
				try {
					const r3 = new FileReader();
					const dataUrl = await new Promise(resolve => { r3.onload = () => resolve(String(r3.result||'')); r3.readAsDataURL(f); });
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { pdfMime = m[1] || pdfMime; pdfBase64 = m[2] || ''; }
				} catch {}
				// Always include the PDF attachment with text/meta and PdfMeta
				list.push({ name: f.name, size: f.size, type: f.type || 'application/pdf', text: finalText, PdfMeta: pdfMeta, Base64: pdfBase64, MimeType: pdfMime });
				// If we rendered images, attach them too so vision models can read
				if (renderedImages.length > 0) {
					for (const img of renderedImages) {
						list.push({ name: `${f.name}-page-${img.page}.png`, size: null, type: 'image/png', text: `Rendered from PDF page ${img.page}.`, Base64: img.base64, MimeType: img.mime });
					}
				}
			} else if (isImage) {
				// Attach images (incl. HEIC/HEIF) with Base64 and empty text to avoid PII false positives
				const r3 = new FileReader();
				const dataUrl = await new Promise(resolve => { r3.onload = () => resolve(String(r3.result||'')); r3.readAsDataURL(f); });
				let base64 = '';
				let mime = f.type || 'image/*';
				try {
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { mime = m[1] || mime; base64 = m[2] || ''; }
				} catch {}
				// Default Vision=on for entitled users (Pro/Team/Ent/Admin)
				list.push({ name: f.name, size: f.size, type: f.type || mime, text: '', Base64: base64, MimeType: mime, useVision: isPaidUser });
			} else if (isZip) {
				// Read as Base64 for server-side ZIP extraction
				const r3 = new FileReader();
				const dataUrl = await new Promise(resolve => { r3.onload = () => resolve(String(r3.result||'')); r3.readAsDataURL(f); });
				let base64 = '';
				let mime = f.type || 'application/zip';
				try {
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { mime = m[1] || mime; base64 = m[2] || ''; }
				} catch {}
				const meta = `ZIP file attached: size ${f.size} bytes.`;
				list.push({ name: f.name, size: f.size, type: f.type || mime, text: meta, Base64: base64, MimeType: mime });
			} else if (isVideo) {
				// Read as Base64; capture duration if possible
				const r3 = new FileReader();
				const dataUrl = await new Promise(resolve => { r3.onload = () => resolve(String(r3.result||'')); r3.readAsDataURL(f); });
				let base64 = '';
				let mime = f.type || '';
				try {
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { mime = mime || m[1]; base64 = m[2] || ''; }
				} catch {}
				let durationSec = null;
				try {
					const url = URL.createObjectURL(f);
					durationSec = await new Promise(resolve => {
						try {
							const video = document.createElement('video');
							video.preload = 'metadata';
							video.onloadedmetadata = () => { resolve(isFinite(video.duration) ? video.duration : null); URL.revokeObjectURL(url); };
							video.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
							video.src = url;
						} catch { resolve(null); }
					});
				} catch { durationSec = null; }
				const meta = `Video file attached: type ${mime || 'unknown'}, size ${f.size} bytes.`;
				list.push({ name: f.name, size: f.size, type: f.type || mime || 'video/*', text: meta, Base64: base64, MimeType: mime, DurationSec: durationSec });
			} else if (isMarkdown) {
				const r = new FileReader();
				const text = await new Promise(resolve => { r.onload = () => resolve(String(r.result || '')); r.readAsText(f); });
				list.push({ name: f.name, size: f.size, type: f.type || 'text/markdown', text });
			} else if (isRtf) {
				const r = new FileReader();
				const text = await new Promise(resolve => { r.onload = () => resolve(String(r.result || '')); r.readAsText(f); });
				list.push({ name: f.name, size: f.size, type: f.type || 'application/rtf', text });
			} else if (isPpt) {
				const meta = `PowerPoint file attached: size ${f.size} bytes.`;
				let base64 = '';
				let mime = f.type || 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
				try {
					const r3 = new FileReader();
					const dataUrl = await new Promise(resolve => { r3.onload = () => resolve(String(r3.result||'')); r3.readAsDataURL(f); });
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { mime = m[1] || mime; base64 = m[2] || ''; }
				} catch {}
				list.push({ name: f.name, size: f.size, type: f.type || mime, text: meta, Base64: base64, MimeType: mime });
			} else if (isAudio) {
				const r = new FileReader();
				const dataUrl = await new Promise(resolve => { r.onload = () => resolve(String(r.result||'')); r.readAsDataURL(f); });
				let base64 = '';
				let mime = f.type || '';
				try {
					const m = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
					if (m) { mime = mime || m[1]; base64 = m[2]; }
				} catch {}
				// Try to read duration using HTMLAudioElement
				let durationSec = null;
				try {
					const url = URL.createObjectURL(f);
					durationSec = await new Promise(resolve => {
						try {
							const audio = new Audio();
							audio.preload = 'metadata';
							audio.onloadedmetadata = () => { resolve(isFinite(audio.duration) ? audio.duration : null); URL.revokeObjectURL(url); };
							audio.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
							audio.src = url;
						} catch { resolve(null); }
					});
				} catch { durationSec = null; }
				const meta = `Audio file attached: type ${mime || 'unknown'}, size ${f.size} bytes.`;
				list.push({ name: f.name, size: f.size, type: f.type || mime || 'audio/*', text: meta, Base64: base64, MimeType: mime, DurationSec: durationSec });
			} else {
				const r = new FileReader();
				const text = await new Promise(resolve => { r.onload = () => resolve(String(r.result || '')); r.readAsText(f); });
				list.push({ name: f.name, size: f.size, type: f.type, text });
			}
		}
		// Add processed files to attachments state, respecting limits
		if (list.length > 0) {
			setAttachments(prev => {
				const cap = isPaidUser ? 10 : 3;
				const merged = [...(prev||[]), ...list];
				return merged.slice(0, cap);
			});
		}
	};

    const resetAioBox = () => {
        // Reset ONLY the AI Output area (right side): output, thread, and stats
        setOutput('');
        setIoText('');
        setThread([]);
        setUsage(null);
        setError('');
        setLastRequestCostUsd(null);
        setSessionRequests(0);
        setSessionTokensIn(0);
        setSessionTokensOut(0);
        setSessionTotalCostUsd(0);
        setTokensRemaining(null);
        try { sessionStorage.setItem('ai_unsaved', '0'); } catch {}
    };

	const handleSaveIo = () => {
		const def = `ai-output-${new Date().toISOString().replace(/[:.]/g,'-')}`;
		setSaveTitle(def);
		setShowSaveModal(true);
	};

	// const handleDownloadTxt = () => { // Reserved for future use
	// 	const blob = new Blob([ioText || output || ''], { type: 'text/plain;charset=utf-8' });
	// 	const url = URL.createObjectURL(blob);
	// 	const a = document.createElement('a');
	// 	a.href = url;
	// 	a.download = `ai-output-${new Date().toISOString().replace(/[:.]/g,'-')}.txt`;
	// 	a.click();
	// 	URL.revokeObjectURL(url);
	// };

	// Export helpers
	const csvEscape = (val) => {
		const s = String(val ?? '');
		if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
		return s;
	};

	const buildCsvFromThread = () => {
		const rows = [];
		rows.push(['role', 'text'].map(csvEscape).join(','));
		const chronological = [...(thread || [])].reverse();
		const userLabel = (userFirstName && userFirstName.trim()) ? userFirstName.trim() : 'You';
		const aiLabel = `AI${model ? ` (${model})` : ''}`;
		if (chronological.length === 0) {
			rows.push(['combined', (ioText || output || prompt || '')].map(csvEscape).join(','));
		} else {
			for (const m of chronological) {
				const label = (m.role === 'ai') ? aiLabel : userLabel;
				rows.push([label, m.text || ''].map(csvEscape).join(','));
			}
		}
		return rows.join('\n');
	};

	const buildJson = () => {
		const payload = {
			provider: providerKey,
			model,
			prompt,
			output: output || null,
			ioText: ioText || null,
			thread: thread || [],
			usage: usage || null,
			session: {
				requests: sessionRequests,
				tokensIn: sessionTokensIn,
				tokensOut: sessionTokensOut,
				totalCostUsd: typeof sessionTotalCostUsd === 'number' ? Number(sessionTotalCostUsd.toFixed(6)) : null
			}
		};
		return JSON.stringify(payload, null, 2);
	};

	// Basic HTML exports that open in Word/Excel (legacy-compatible). Saved as .docx/.xlsx but content is HTML.
	const buildDocHtml = () => {
		const safe = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
		const userLabel = (userFirstName && userFirstName.trim()) ? userFirstName.trim() : 'You';
		const aiLabel = `AI${model ? ` (${model})` : ''}`;
		const parts = (thread || []).map(m => {
			const label = (m.role === 'ai') ? aiLabel : userLabel;
		return `<div style="margin:0 0 6px 0;"><strong>${safe(label)}</strong></div><pre style="white-space:pre-wrap;border:1px solid #ddd;padding:8px;background:#f9f9f9;">${safe(m.text)}</pre>`;
	}).join('\\n');
	const body = parts || `<pre style="white-space:pre-wrap;">${safe(ioText || output || prompt || '')}</pre>`;
	const style = `<style>body{font-family: Aptos, Arial, Helvetica, sans-serif; font-size:12pt;} pre{font-family: Aptos, Arial, Helvetica, sans-serif; font-size:12pt;}</style>`;
	return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>AI I/O</title>${style}</head><body>${body}</body></html>`;
	};

	const buildExcelHtml = () => {
		const safe = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
		const userLabel = (userFirstName && userFirstName.trim()) ? userFirstName.trim() : 'You';
		const aiLabel = `AI${model ? ` (${model})` : ''}`;
		const rows = (thread && thread.length > 0 ? [...thread] : [{ role: 'combined', text: (ioText || output || prompt || '') }])
			.map(m => `<tr><td><strong>${safe((m.role === 'ai') ? aiLabel : userLabel)}</strong></td><td>${safe(m.text||'')}</td></tr>`).join('');
		const style = `<style>body, table, td, th{font-family: Aptos, Arial, Helvetica, sans-serif; font-size:12pt;}</style>`;
		return `<!DOCTYPE html><html><head><meta charset="utf-8">${style}</head><body><table border="1"><thead><tr><th>role</th><th>text</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
	};

	// True .xlsx generation via dynamic import (requires installing 'xlsx')
	const buildRealXlsxBlob = async () => {
		try {
			const XLSX = await import('xlsx');
			const userLabel = (userFirstName && userFirstName.trim()) ? userFirstName.trim() : 'You';
			const aiLabel = `AI${model ? ` (${model})` : ''}`;
			const ordered = (thread && thread.length > 0) ? [...thread] : [{ role: 'combined', text: (ioText || output || prompt || '') }];
			const rows = [['role', 'text'], ...ordered.map(m => [(m.role === 'ai') ? aiLabel : userLabel, m.text || ''])];
			const ws = XLSX.utils.aoa_to_sheet(rows);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, 'AI I-O');
			const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
			return new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		} catch {
			return null;
		}
	};

	// True .docx generation via dynamic import (requires installing 'docx')
	const buildRealDocxBlob = async () => {
		try {
			const mod = await import('docx');
			const { Document, Packer, Paragraph, TextRun } = mod;
			const children = [];
			const userLabel = (userFirstName && userFirstName.trim()) ? userFirstName.trim() : 'You';
			const aiLabel = `AI${model ? ` (${model})` : ''}`;
			const ordered = (thread && thread.length > 0) ? [...thread] : [{ role: 'combined', text: (ioText || output || prompt || '') }];
			for (const m of ordered) {
				const label = (m.role === 'ai') ? aiLabel : userLabel;
				children.push(new Paragraph({ children: [ new TextRun({ text: label, bold: true }) ] }));
				const lines = String(m.text || '').split(/\r?\n/);
				for (const line of lines) {
					children.push(new Paragraph({ children: [ new TextRun({ text: line }) ] }));
				}
			}
			const doc = new Document({
				styles: {
					default: {
						document: { run: { font: 'Aptos', size: 24 } },
						heading1: { run: { font: 'Aptos' } },
						heading2: { run: { font: 'Aptos' } },
						heading3: { run: { font: 'Aptos' } }
					}
				},
				sections: [{ properties: {}, children }]
			});
			const blob = await Packer.toBlob(doc);
			return blob;
		} catch {
			return null;
		}
	};

	const saveBlobWithPicker = async (blob, suggestedName, types) => {
		try {
			if (window.showSaveFilePicker) {
				const handle = await window.showSaveFilePicker({ suggestedName, types });
				const writable = await handle.createWritable();
				await writable.write(blob);
				await writable.close();
				return true;
			}
		} catch (e) {
			// If user cancelled, respect it and do not fallback to anchor
			if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
				return false;
			}
			// otherwise fallthrough to anchor download
		}
		try {
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = suggestedName;
			a.click();
			URL.revokeObjectURL(url);
			return true;
		} catch {
			return false;
		}
	};

	const handleExportIo = async () => {
		const baseName = sanitizeFileName(buildTitleFromContent(prompt || output || ioText || 'AI Output'));
		const paid = !!isPaidUser;
		let defaultExt = paid ? '.csv' : '.txt';
		// Load user preference for default export format when available
		try {
			const t = authService.getToken && authService.getToken();
			const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
			if (res.ok) {
				const prefs = await res.json();
				const def = (prefs?.DefaultExportFormat || '').toLowerCase();
				if (['csv','json','txt','xlsx','docx'].includes(def)) {
					defaultExt = `.${def}`;
				}
			}
		} catch {}
		const types = paid ? [
			{ description: 'CSV', accept: { 'text/csv': ['.csv'] } },
			{ description: 'JSON', accept: { 'application/json': ['.json'] } },
			{ description: 'Text', accept: { 'text/plain': ['.txt'] } },
			{ description: 'Excel (.xlsx)', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } },
			{ description: 'Word (.docx)', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } },
			{ description: 'Excel (.xls)', accept: { 'application/vnd.ms-excel': ['.xls'] } },
			{ description: 'Word (.doc)', accept: { 'application/msword': ['.doc'] } }
		] : [
			{ description: 'Text', accept: { 'text/plain': ['.txt'] } }
		];
		let chosenExt = defaultExt;
		let fileName = `${baseName}${defaultExt}`;
		let saved = false;
		if (window.showSaveFilePicker) {
			try {
				const handle = await window.showSaveFilePicker({ suggestedName: fileName, types });
				fileName = handle.name || fileName;
				chosenExt = (fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : defaultExt).toLowerCase();
				// Prefer real .docx/.xlsx if libraries are available; otherwise map to legacy
				if (chosenExt === '.docx') {
					const docxBlob = await buildRealDocxBlob();
					if (docxBlob) {
						const writable = await handle.createWritable();
						await writable.write(docxBlob);
						await writable.close();
						saved = true;
						setToast({ show:true, type:'success', message:'Exported.' });
						return;
					} else {
						setToast({ show:true, type:'warning', message:'docx package not installed. Exporting as .doc instead.' });
						chosenExt = '.doc';
					}
				}
				if (chosenExt === '.xlsx') {
					const xlsxBlob = await buildRealXlsxBlob();
					if (xlsxBlob) {
						const writable = await handle.createWritable();
						await writable.write(xlsxBlob);
						await writable.close();
						saved = true;
						setToast({ show:true, type:'success', message:'Exported.' });
						return;
					} else {
						setToast({ show:true, type:'warning', message:'xlsx package not installed. Exporting as .xls instead.' });
						chosenExt = '.xls';
					}
				}
				let blob;
				if (!paid && chosenExt !== '.txt') {
					setToast({ show:true, type:'warning', message:'Free plan: exporting as .txt' });
					chosenExt = '.txt';
				}
				switch (chosenExt) {
					case '.txt':
						blob = new Blob([ioText || output || prompt || ''], { type: 'text/plain;charset=utf-8' });
						break;
					case '.csv': {
						const csv = buildCsvFromThread();
						blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
						break;
					}
					case '.json': {
						const json = buildJson();
						blob = new Blob([json], { type: 'application/json;charset=utf-8' });
						break;
					}
					case '.xls': {
						const html = buildExcelHtml();
						blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
						break;
					}
					case '.doc': {
						const html = buildDocHtml();
						blob = new Blob([html], { type: 'application/msword;charset=utf-8' });
						break;
					}
					default: {
						const csv = buildCsvFromThread();
						blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
						break;
					}
				}
				const writable = await handle.createWritable();
				await writable.write(blob);
				await writable.close();
				saved = true;
				setToast({ show:true, type:'success', message:'Exported.' });
				return;
			} catch (e) {
				if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
					// user cancelled – do nothing
					return;
				}
				// fall back to manual download below
			}
		}
		// Fallback: avoid native prompt; use default preference and toast
		const ext = (paid ? (defaultExt.replace('.', '') || 'csv') : 'txt').toLowerCase();
		if (!paid && ext !== 'txt') setToast({ show:true, type:'warning', message:'Free plan: exporting as .txt' });
		let blob;
		switch (ext) {
			case 'txt':
				blob = new Blob([ioText || output || prompt || ''], { type: 'text/plain;charset=utf-8' });
				fileName = `${baseName}.txt`;
				break;
			case 'json':
				blob = new Blob([buildJson()], { type: 'application/json;charset=utf-8' });
				fileName = `${baseName}.json`;
				break;
			case 'xlsx': {
				const xlsxBlob = await buildRealXlsxBlob();
				if (xlsxBlob) {
					const ok = await saveBlobWithPicker(xlsxBlob, `${baseName}.xlsx`, types);
					if (ok) setToast({ show:true, type:'success', message:'Exported.' });
					return;
				} else {
					setToast({ show:true, type:'warning', message:'xlsx package not installed. Exporting as .xls instead.' });
					blob = new Blob([buildExcelHtml()], { type: 'application/vnd.ms-excel;charset=utf-8' });
					fileName = `${baseName}.xls`;
					break;
				}
			}
			case 'xls':
				blob = new Blob([buildExcelHtml()], { type: 'application/vnd.ms-excel;charset=utf-8' });
				fileName = `${baseName}.xls`;
				break;
			case 'docx': {
				const docxBlob = await buildRealDocxBlob();
				if (!docxBlob) { setToast({ show:true, type:'warning', message:'docx package not installed. Exporting as .doc instead.' }); }
				if (docxBlob) {
					const ok = await saveBlobWithPicker(docxBlob, `${baseName}.docx`, types);
					if (ok) setToast({ show:true, type:'success', message:'Exported.' });
					return;
				}
				blob = new Blob([buildDocHtml()], { type: 'application/msword;charset=utf-8' });
				fileName = `${baseName}.doc`;
				break;
			}
			case 'doc':
				blob = new Blob([buildDocHtml()], { type: 'application/msword;charset=utf-8' });
				fileName = `${baseName}.doc`;
				break;
			case 'csv':
			default:
				blob = new Blob([buildCsvFromThread()], { type: 'text/csv;charset=utf-8' });
				fileName = `${baseName}.csv`;
		}
		saved = await saveBlobWithPicker(blob, fileName, types);
		if (saved) setToast({ show:true, type:'success', message:'Exported.' });
	};

	// const totalTokens = (usage?.inputTokens || 0) + (usage?.outputTokens || 0); // Reserved for future use

	const threadRef = useRef(null);
	useEffect(() => {
		if (threadRef.current) {
			try { threadRef.current.scrollTop = 0; } catch {}
		}
	}, [thread, pending]);

	// Keyboard shortcuts: F6 or Ctrl+Shift+M toggle Express/Studio mode
	useEffect(() => {
		const handler = (e) => {
			// Inactive when any modal is open
			if (document.querySelector('.app-modal')) return;
			const isF6 = e.key === 'F6';
			const isCtrlShiftM = e.ctrlKey && e.shiftKey && (e.key === 'M' || e.key === 'm');
			if (!isF6 && !isCtrlShiftM) return;
			e.preventDefault();
			
			// Check tier access for Studio mode
			if (!canAccessFeature('CHAT_STUDIO')) {
				setToast({ 
					show: true, 
					type: 'warning', 
					message: getFeatureDenialMessage('CHAT_STUDIO')
				});
				return;
			}
			
			setIsStudioMode(m => !m);
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [canAccessFeature, getFeatureDenialMessage]);

	// Handle closing tuning dropdown on outside click or Escape key
	useEffect(() => {
		if (!tuningMenuOpen) return;
		const handleClickOutside = (e) => {
			const tuningPill = e.target.closest('[aria-expanded="true"]');
			const tuningPanel = e.target.closest('[role="dialog"]');
			if (!tuningPill && !tuningPanel) {
				setTuningMenuOpen(false);
			}
		};
		const handleEscape = (e) => {
			if (e.key === 'Escape' && tuningMenuOpen) {
				setTuningMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [tuningMenuOpen]);

	// Drag-and-drop handlers for file upload
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDragging) {
			setIsDragging(true);
		}
	};

	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDragging) {
			setIsDragging(true);
		}
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		// Only clear dragging state if we're actually leaving the drop zone (not moving to a child)
		// Check if relatedTarget is outside the current target
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsDragging(false);
		}
	};

	const handleDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		
		const files = Array.from(e.dataTransfer.files || []);
		if (files.length > 0) {
			try {
				await processFiles(files);
			} catch (err) {
				setToast && setToast({ show: true, type: 'error', message: 'Failed to process dropped files.' });
			}
		}
	};

	return (
		<>
		  <div className="ai-container ai-grid">
			<div className="col-left">
			  <div 
				id="prompt-input" 
				className={`panel drop-zone ${isDragging ? 'drop-zone-active' : ''}`}
				style={{ display: 'flex', flexDirection: 'column', flex: 1, position: 'relative' }}
				onDragOver={handleDragOver}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				{isDragging && (
					<div className="drop-zone-overlay">
						<div className="drop-zone-text">Drop Files Here...</div>
					</div>
				)}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
					<h4 style={{ margin: 0 }}>Prompt Input</h4>
					<button
						onClick={() => setShowPromptGeneratorModal(true)}
						style={{
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
							color: 'white',
							border: 'none',
							padding: '10px 20px',
							borderRadius: '8px',
							fontSize: '0.95rem',
							fontWeight: 600,
							cursor: 'pointer',
							transition: 'all 0.3s ease',
							boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
						}}
					>
						Open Prompt Generator
					</button>
				</div>
                <label htmlFor="prompt-input-text" style={{ position:'absolute', left:'-10000px', top:'auto', width:1, height:1, overflow:'hidden' }}>Prompt input</label>
                <textarea id="prompt-input-text" value={prompt} onChange={e => setPrompt(e.target.value)} className="prompt-input" placeholder="Type your prompt..." style={{ flex: 1 }} />
				<div className="actions" style={{ display: 'flex', alignItems: 'center' }}>
				  <div style={{ display: 'flex', alignItems: 'center' }}>
				    <button title={sendBlocked ? 'Inputs exceed cap. Remove images or lower Max Output.' : 'Send to AI'} onClick={() => onSubmit(false)} disabled={!prompt.trim() || pending || sendBlocked}>{pending ? '⏳ Sending…' : 'Send'}</button>
				    <button title="Save prompt" onClick={savePromptInputAsync} style={{ marginLeft: 8 }}>Save</button>
				    <button
              title="Clear"
              onClick={() => {
                setConfirmToast({
                  show: true,
                  message: 'Reset prompt and tuning?',
                  onConfirm: () => {
                    setPrompt('');
                    setTemperature(0.7);
                    setTopP(0.9);
                    setConfirmToast(prev => ({ ...prev, show: false }));
                  },
                  onCancel: () => setConfirmToast(prev => ({ ...prev, show: false }))
                });
              }}
              style={{ marginLeft: 8 }}
            >
              Reset
            </button>
				  </div>
				  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
				    <span style={{ display: 'inline-block', width: 1, height: 20, background: '#ddd', marginRight: 8, marginLeft: 8 }}></span>
				    <button
				      title="Use this to optimize your own prompts!"
				      onClick={optimizePrompt}
				      disabled={optimizing || optimizeLocked || (prompt.trim().length <= 30)}
				      style={{ marginLeft: 0, backgroundColor: '#dc3545', color: '#fff' }}
				    >
				      {optimizing ? 'Optimizing…' : 'Optimize'}
				    </button>
				  </div>
				</div>
				{/* Second row under actions: Multi-send (Pro only, Studio mode only) */}
				{(() => {
					const user = authService.getCurrentUser();
					const roles = (user && user.roles) ? user.roles.map(r => String(r).toLowerCase()) : [];
					const isAdmin = roles.includes('admin');
					return (canAccessFeature('MULTI_SEND') || isAdmin) && isStudioMode;
				})() && (
				  <div style={{ marginTop: 8, display:'flex', alignItems:'center', gap:8 }}>
                <button
                  onClick={()=>{ setShowMultiSend(true); }}
                  disabled={!prompt.trim() || pending}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #667eea', background: '#c99edb', color: '#3730a3', cursor: (!prompt.trim()||pending)?'not-allowed':'pointer' }}
                  title="Send this prompt to multiple AI models"
                >
				      Multi-send
				    </button>
				  </div>
				)}
				{error && <div style={{ color: 'crimson', marginTop: 8 }}>{error}</div>}
			  </div>
			</div>
	  
			<div className="col-right">
			  <div className="panel output-panel">
				<div className="io-header">
				  <div className="row-top" style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center', gap:10 }}>
					<div className="header-left" style={{ display:'flex', alignItems:'center', gap:10 }}>
					  <img
						src={isStudioMode ? '/Studio.png' : '/Express.png'}
						alt={isStudioMode ? 'Studio Mode' : 'Express Mode'}
						className="mode-toggle-image"
						onClick={() => {
							if (!canAccessFeature('CHAT_STUDIO')) {
								setToast({ 
									show: true, 
									type: 'warning', 
									message: getFeatureDenialMessage('CHAT_STUDIO')
								});
								return;
							}
							setIsStudioMode(m => !m);
						}}
						title="Click to switch modes (F6 or Ctrl+Shift+M)"
						style={{ width: '200px', height: 'auto', cursor: 'pointer', transition: 'opacity 0.3s ease' }}
					  />
					</div>
					<div className="io-actions header-buttons" style={{ display:'flex', alignItems:'center', gap:8, justifySelf:'center' }}>
					  <button className="save-output-btn" onClick={handleSaveIo} title="Save I/O">Save</button>
					  <button className="recall-btn" onClick={async () => { await refreshSavedList(); setShowRecall(true); }} title="Recall I/O">Recall</button>
					  <button className="download-btn" onClick={handleExportIo} title="Export (Save As)" disabled={pending}>Export</button>
					</div>
                    <div className="header-right" style={{ display:'flex', alignItems:'center', gap:10, justifySelf:'end' }}>
                      {/** Short model label formatter to keep dropdown compact while value remains full ID */}
                      <select className="model-select" value={`${providerKey}::${model}`} onChange={e => {
                        const [prov, m] = e.target.value.split('::');
                        const isFree = !isPaidUser;
                        const provLower = String(prov||'').toLowerCase();
                        const mLower = String(m||'').toLowerCase();
                        const freeAllowed = (provLower === 'openai' && (mLower === 'gpt-4o-mini' || mLower.startsWith('gpt-4o-mini-'))) || (provLower === 'gemini' && (mLower === 'gemini-2.5-flash' || mLower.startsWith('gemini-2.5-flash')));
                        if (isFree && !freeAllowed) {
                          try { setToast({ show:true, type:'warning', message:'Free plan: This model is available on paid plans. Please select GPT-4o-mini or Gemini 2.5 Flash.' }); } catch{}
                          return; // do not change selection
                        }
                        setProviderKey(prov); setModel(m);
                        localStorage.setItem('ai_provider', prov); localStorage.setItem('ai_model', m);
                      }}>
                        {providers.flatMap(p => {
                          const provLower = String(p.key||'').toLowerCase();
                          return [
                            <option key={`${p.key}::header`} value={`${p.key}::header`} disabled>{p.name}</option>,
                            ...((p.allowed || []).map(m => {
                              const mLower = String(m||'').toLowerCase();
                              const freeAllowed = (provLower === 'openai' && (mLower === 'gpt-4o-mini' || mLower.startsWith('gpt-4o-mini-'))) || (provLower === 'gemini' && (mLower === 'gemini-2.5-flash' || mLower.startsWith('gemini-2.5-flash')));
                              const disabled = (!isPaidUser && !freeAllowed);
                              const full = m;
                              const shortLabel = (() => {
                                try {
                                  const id = String(full||'');
                                  const lc = id.toLowerCase();
                                  if (lc.startsWith('llama-4-scout')) return 'Llama 4 Scout';
                                  if (lc.startsWith('llama-4-maverick')) return 'Llama 4 Maverick';
                                  if (lc === 'mistral-large-latest') return 'Mistral Large';
                                  if (lc.startsWith('claude-haiku-4-5')) return 'Claude Haiku 4.5';
                                  if (lc.startsWith('claude-sonnet-4-5')) return 'Claude Sonnet 4.5';
                                  if (lc.startsWith('claude-opus-4-1')) return 'Claude Opus 4.1';
                                  return id.replace(/-latest$/,'').replace(/(^|\\b)([a-z])/g,(m0, p, c)=>p + c.toUpperCase());
                                } catch { return full; }
                              })();
                              const label = disabled ? `${shortLabel} (Paid)` : shortLabel;
                              return (<option key={`${p.key}::${m}`} value={`${p.key}::${m}`} title={full} disabled={disabled}>{label}</option>);
                            }))
                          ];
                        })}
                      </select>
                    </div>
				  </div>
                  <div className="row-bottom" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', justifyContent:'center' }}>
                    <button
                      className="mode-pill"
                      onClick={cancelInFlight}
                      disabled={!pending}
                      title="Cancel the current AI request"
                      style={{ padding:'4px 10px', borderRadius:999, border:'1px solid #fca5a5', background:'#fee2e2', color:'#b91c1c' }}
                    >
                      Stop
                    </button>
					<button className="mode-pill" onClick={() => { chatMode ? exitChatMode() : openChatModal(); }} title={chatMode ? 'Chat mode (click to switch to Work)' : 'Work mode (click to switch to Chat)'} style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #ccc', background: chatMode ? '#fee2e2' : '#e6ffed', color: chatMode ? '#b91c1c' : '#065f46' }}>
						{chatMode ? 'Chat' : 'Work'}
					</button>
                    {isStudioMode && (
                      <label className="mode-pill" title="DLP: Do not store content" style={{ padding:'4px 10px', borderRadius: 999, border:'1px solid #ccc', background:'#f3f4f6', color:'#374151', cursor:'pointer', transition: 'opacity 0.3s ease, transform 0.3s ease' }}>
                        <input type="checkbox" checked={doNotStore} onChange={e=>setDoNotStore(e.target.checked)} style={{ marginRight:6 }} />
                        Don't store
                      </label>
                    )}
                    {canAccessFeature('CHAT_STUDIO') && isStudioMode && (
                      <span
                        className="mode-pill"
                        role="button"
                        tabIndex={0}
                        onClick={()=>{
							if (tierInfo.tier === 'Free') {
								setToast({ show: true, type: 'warning', message: 'Upgrade to Pro to raise input limits' });
								return;
							}
							setShowOverride(true);
						}}
                        onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') {
							if (tierInfo.tier === 'Free') {
								setToast({ show: true, type: 'warning', message: 'Upgrade to Pro to raise input limits' });
								return;
							}
							setShowOverride(true);
						}}}
                        title={`Input Limit: ${tierInfo.limits.inputCap ? `${tierInfo.limits.inputCap.toLocaleString()} tokens` : 'Unlimited'}`}
                        style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid #ccc', background: '#eef2ff', color: '#1e3a8a', cursor: 'pointer', transition: 'opacity 0.3s ease, transform 0.3s ease' }}
                      >
                        ↑Limits {tierInfo.limits.inputCap ? `(${Math.floor(tierInfo.limits.inputCap/1000)}K)` : '(Unlimited)'}
                      </span>
                    )}
						<input ref={fileInputRef} type="file" multiple accept="application/pdf,image/*,audio/*,video/*,.docx,.xlsx,.xls,.ppt,.pptx,.md,.markdown,.rtf,.zip,application/zip" style={{ display:'none' }} onChange={async (e) => {
						try {
							const files = Array.from(e.target.files || []);
							if (files.length === 0) return;
							await processFiles(files);
							// Reset file input to allow selecting same file again
							e.target.value = '';
						} catch {}
					}} />
                    <button className="mode-pill" onClick={() => fileInputRef.current && fileInputRef.current.click()} title={(!isPaidUser && attachments && attachments.length >= 3) ? 'Free: up to 3 files per request' : 'Attach files'} style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid #ccc', background: '#eef2ff', color: '#1e3a8a' }}>
                        Drop Files{attachments && attachments.length ? ` (${attachments.length}${!isPaidUser && attachments.length>=3 ? ' / 3' : ''})` : ''}
                    </button>
                    {isStudioMode && (
                      <span
                        className="mode-pill"
                        role="button"
                        tabIndex={0}
                        aria-haspopup="listbox"
                        aria-expanded={ocrMenuOpen ? 'true' : 'false'}
                        onClick={()=> setOcrMenuOpen(v=>!v)}
                        onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') setOcrMenuOpen(v=>!v); }}
                        title={ocrIfEmpty ? 'OCR: On (fallback for textless PDFs)' : 'OCR: Off'}
                        style={{
                          background: ocrIfEmpty ? '#10b981' : '#eef2ff',
                          color: ocrIfEmpty ? '#ffffff' : '#111827',
                          border: '1px solid ' + (ocrIfEmpty ? '#059669' : '#ccc'),
                          position:'relative',
                          transition: 'opacity 0.3s ease, transform 0.3s ease'
                        }}
                      >
                        {ocrIfEmpty ? `OCR: ${(ocrLangs||'eng').toLowerCase()}` : 'OCR: Off'}
                        {ocrMenuOpen && (
                          <div role="listbox" aria-label="OCR options" style={{ position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:1000, background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 8px 16px rgba(0,0,0,0.08)', minWidth:180 }}>
                            <button role="option" aria-selected={ocrIfEmpty && (ocrLangs||'').toLowerCase()==='eng'}
                              onClick={(e)=>{ e.stopPropagation(); setOcrIfEmpty(true); setOcrLangs('eng'); setOcrMenuOpen(false); try { setToast({ show:true, type:'info', message:'OCR: On (eng)' }); } catch{} }}
                              style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 12px', background:'none', border:'none', cursor:'pointer' }}
                            >English (eng)</button>
                            <button role="option" aria-selected={ocrIfEmpty && (ocrLangs||'').toLowerCase()==='eng,spa'}
                              onClick={(e)=>{ e.stopPropagation(); setOcrIfEmpty(true); setOcrLangs('eng,spa'); setOcrMenuOpen(false); try { setToast({ show:true, type:'info', message:'OCR: On (eng, spa)' }); } catch{} }}
                              style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 12px', background:'none', border:'none', cursor:'pointer' }}
                            >English + Spanish (eng, spa)</button>
                            <div style={{ height:1, background:'#e5e7eb', margin:'4px 0' }}></div>
                            <button role="option" aria-selected={!ocrIfEmpty}
                              onClick={(e)=>{ e.stopPropagation(); setOcrIfEmpty(false); setOcrMenuOpen(false); try { setToast({ show:true, type:'info', message:'OCR: Off' }); } catch{} }}
                              style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 12px', background:'none', border:'none', cursor:'pointer', color:'#6b7280' }}
                            >Turn Off</button>
                          </div>
                        )}
                      </span>
                    )}
                    {isStudioMode && (() => {
                      // Calculate visual state for Tuning pill
                      const defaultTemp = 0.7;
                      const isDefaultTemp = Math.abs(temperature - defaultTemp) < 0.01;
                      const isDefaultTopP = Math.abs(topP - 0.9) < 0.01;
                      const isExtremeTemp = temperatureScale === 1 
                        ? (temperature < 0.1 || temperature > 0.9)
                        : (temperature < 0.1 || temperature > 1.9);
                      const isExtremeTopP = topP < 0.1 || topP > 0.95;
                      const isExtreme = isExtremeTemp || isExtremeTopP;
                      const isNonDefault = !isDefaultTemp || !isDefaultTopP;
                      const pillBg = isExtreme ? '#fee2e2' : (isNonDefault ? '#fce7f3' : '#eef2ff');
                      const pillColor = isExtreme ? '#b91c1c' : (isNonDefault ? '#be185d' : '#1e3a8a');
                      const pillBorder = isExtreme ? '#fca5a5' : (isNonDefault ? '#f9a8d4' : '#ccc');
                      return (
                        <span
                          className="mode-pill"
                          role="button"
                          tabIndex={0}
                          aria-haspopup="dialog"
                          aria-expanded={tuningMenuOpen ? 'true' : 'false'}
                          onClick={()=> setTuningMenuOpen(v=>!v)}
                          onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') setTuningMenuOpen(v=>!v); }}
                          title={`Temp: ${temperature.toFixed(1)} | Top P: ${topP.toFixed(2)}`}
                          style={{
                            background: pillBg,
                            color: pillColor,
                            border: `1px solid ${pillBorder}`,
                            position:'relative',
                            transition: 'opacity 0.3s ease, transform 0.3s ease'
                          }}
                        >
                          Tuning
                          {tuningMenuOpen && (
                            <div 
                              role="dialog" 
                              aria-label="Tuning parameters" 
                              onClick={(e) => e.stopPropagation()}
                              style={{ 
                                position:'absolute', 
                                top:'calc(100% + 6px)', 
                                left:'-250px', 
                                zIndex:1000, 
                                background:'#fff', 
                                border:'1px solid #e5e7eb', 
                                borderRadius:8, 
                                boxShadow:'0 8px 16px rgba(0,0,0,0.08)', 
                                minWidth:400,
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px'
                              }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                  <span title="Controls randomness. Lower = more focused and deterministic, Higher = more creative and varied" style={{ color: '#111827' }}>Temperature</span>
                                  <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '13px' }}>{temperature.toFixed(1)}</span>
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max={temperatureScale === 1 ? "1" : "2"}
                                  step="0.1"
                                  value={temperature}
                                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                                  style={{ width: '100%' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9ca3af' }}>
                                  <span>0</span>
                                  <span>{temperatureScale === 1 ? "1" : "2"}</span>
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                  <span title="Controls diversity by limiting token choices. Lower = more focused, Higher = more diverse" style={{ color: '#111827' }}>Top P</span>
                                  <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '13px' }}>{topP.toFixed(2)}</span>
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={topP}
                                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                                  style={{ width: '100%' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9ca3af' }}>
                                  <span>0.0</span>
                                  <span>1.0</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </span>
                      );
                    })()}
                    {/* file name pills moved to a dedicated row below */}
					{chatMode && (
					  <>
					    <span className="chip cost" title="Chat Cutoff (CCO)">CCO: <strong>{`$${((Number(chatCcoCents||0))/100).toFixed(2)}`}</strong></span>
					    <button onClick={openChatModal} style={{ background:'none', border:'1px solid #0d6efd', color:'#0d6efd', cursor:'pointer', padding:'2px 8px', borderRadius:999 }}>Edit CCO</button>
					    {archivedCount > 0 && (
					      <span 
					        className="chip" 
					        role="button"
					        tabIndex={0}
					        onClick={() => {
					          // Temporarily expand context by 5 pairs (10 messages)
					          setTempContextExpansion(prev => prev + 10);
					        }}
					        onKeyDown={(e) => {
					          if (e.key === 'Enter' || e.key === ' ') {
					            setTempContextExpansion(prev => prev + 10);
					          }
					        }}
					        title="Click to load more context (expands by 5 message pairs for next send)"
					        style={{ 
					          background:'#fef3c7', 
					          color:'#92400e', 
					          cursor:'pointer', 
					          padding:'4px 10px', 
					          borderRadius:999,
					          border:'1px solid #fbbf24',
					          transition: 'opacity 0.3s ease, transform 0.3s ease'
					        }}
					      >
					        More: {archivedCount}
					      </span>
					    )}
					  </>
					)}
                    {!isStudioMode && <BalanceChip tick={usageRefreshTick} />}
                  </div>
                  <div className="row-metrics" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', justifyContent:'center', marginTop:6 }}>
                    {isStudioMode && (
                      <span className="mode-pill" role="button" tabIndex={0} onClick={()=>setShowSessionDetails(v=>!v)} onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') setShowSessionDetails(v=>!v); }} title="Show session totals" style={{ transition: 'opacity 0.3s ease, transform 0.3s ease' }}>Session</span>
                    )}
                    {isStudioMode && <BalanceChip tick={usageRefreshTick} />}
                    {isStudioMode && (
                      <span className="mode-pill" role="button" tabIndex={0} onClick={()=>setShowCostDetails(v=>!v)} onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') setShowCostDetails(v=>!v); }} title="Show cost breakdown" style={{ transition: 'opacity 0.3s ease, transform 0.3s ease' }}>Details</span>
                    )}
                    {preflightWarn && (
                      <span className="mode-pill" title={preflightWarn} style={{ background:'#fef9c3', color:'#111827' }}>
                        Input cap
                      </span>
                    )}
                    {attachments && attachments.length > 0 && (
                      <span className="mode-pill" role="button" tabIndex={0} onClick={()=>setShowAttachDrawer(s=>!s)} onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') setShowAttachDrawer(s=>!s); }} title="Show attachment options" aria-expanded={showAttachDrawer}>
                        Attachments {attachments.length ? `(${attachments.length})` : ''}
                      </span>
                    )}
                    {modStatus && (
                      <span className="mode-pill" title={`Moderation: ${modStatus.decision}${modStatus.source?` (${modStatus.source})`:''}`}
                        style={{ background: modStatus.decision==='block' ? '#fee2e2' : (modStatus.decision==='warn' ? '#fef9c3' : '#eef2ff'), color: '#111827' }}>
                        Moderation: {modStatus.decision}
                      </span>
                    )}
                  </div>
                  {showSessionDetails && (
                    <div className="session-details" style={{ marginTop:6, border:'1px solid #e5e7eb', borderRadius:8, padding:10, background:'#fafafa' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', rowGap:4, columnGap:10, fontSize:13 }}>
                        <div style={{ color:'#6b7280' }}>Requests</div><div>{sessionRequests2}</div>
                        <div style={{ color:'#6b7280' }}>Tokens In</div><div>{sessionInTokens2}</div>
                        <div style={{ color:'#6b7280' }}>Tokens Out</div><div>{sessionOutTokens2}</div>
                        <div style={{ color:'#6b7280' }}>Cost</div><div>${((sessionCostCents2||0)/100).toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                  {showCostDetails && (
                    <div className="cost-details" style={{ marginTop:6, border:'1px solid #e5e7eb', borderRadius:8, padding:10, background:'#fafafa' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', rowGap:4, columnGap:10, fontSize:13 }}>
                        <div style={{ color:'#6b7280' }}>Provider</div><div>{providerKey}{model ? ` (${model})` : ''}</div>
                        <div style={{ color:'#6b7280' }}>Tokens In</div><div>{usage?.inputTokens || 0}</div>
                        <div style={{ color:'#6b7280' }}>Tokens Out</div><div>{usage?.outputTokens || 0}</div>
                        <div style={{ color:'#6b7280' }}>Rates</div><div>{currentPrice ? `In ${currentPrice.inStr} • Out ${currentPrice.outStr}` : '—'}</div>
                        <div style={{ color:'#6b7280' }}>Cost (actual)</div><div>{formatCostUsd(typeof lastRequestCostUsd === 'number' ? lastRequestCostUsd : null)}</div>
                        <div style={{ color:'#6b7280' }}>RequestId</div><div>{lastRequestId || '—'}</div>
                        <div style={{ gridColumn:'1 / span 2', borderTop:'1px dashed #ddd', margin:'8px 0' }}></div>
                        <div style={{ fontWeight:600 }}>Per-request cap</div><div></div>
                        <div style={{ color:'#6b7280' }}>Cap</div>
                        <div>
                          <input type="range" min={0} max={2000} step={25} value={Number.isFinite(capCents)?capCents:0} onChange={(e)=>setCapCents(parseInt(e.target.value,10))} aria-label="Per-request cap in cents" />
                          <span style={{ marginLeft:8 }}>${((Number(capCents||0))/100).toFixed(2)}</span>
                          <button onClick={async()=>{
                            try {
                              const t = authService.getToken && authService.getToken();
                              const r = await fetch('/api/userpreferences', { method:'POST', headers:{ 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }:{}) }, body: JSON.stringify({ perRequestCapCents: Number.isFinite(capCents)?capCents:null }), credentials:'include' });
                              if (!r.ok) throw new Error('Failed to save');
                              setToast({ show:true, type:'success', message:'Per-request cap saved.'});
                            } catch (e) { setToast({ show:true, type:'error', message:e.message||'Save failed'}); }
                          }} style={{ marginLeft:10, padding:'2px 8px', border:'1px solid #e5e7eb', borderRadius:6, background:'#fff', cursor:'pointer' }}>Save</button>
                          <button onClick={async()=>{
                            try {
                              const t = authService.getToken && authService.getToken();
                              const r = await fetch('/api/userpreferences', { method:'POST', headers:{ 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }:{}) }, body: JSON.stringify({ perRequestCapCents: null }), credentials:'include' });
                              if (!r.ok) throw new Error('Failed to clear');
                              setCapCents(null);
                              setToast({ show:true, type:'success', message:'Cap cleared.'});
                            } catch (e) { setToast({ show:true, type:'error', message:e.message||'Clear failed'}); }
                          }} style={{ marginLeft:6, padding:'2px 8px', border:'1px solid #e5e7eb', borderRadius:6, background:'#fff', cursor:'pointer' }}>Clear</button>
                        </div>
                        <div style={{ color:'#6b7280' }}>Stop at cap</div>
                        <div>
                          <label style={{ display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                            <input type="checkbox" checked={!!stopAtCap} onChange={(e)=>setStopAtCap(e.target.checked)} aria-label="Stop at cap" />
                            <span>Block request if estimate exceeds cap</span>
                          </label>
                        </div>
                      </div>
                      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                        <button className="mode-pill" onClick={()=>{
                          try {
                            const rows = [
                              `Provider: ${providerKey}${model?` (${model})`:''}`,
                              `Tokens In: ${usage?.inputTokens||0}`,
                              `Tokens Out: ${usage?.outputTokens||0}`,
                              `Rates: ${currentPrice?`In ${currentPrice.inStr} • Out ${currentPrice.outStr}`:'—'}`,
                              `Cost: ${formatCostUsd(typeof lastRequestCostUsd==='number'?lastRequestCostUsd:null)}`,
                              `RequestId: ${lastRequestId||'—'}`,
                              `Session: req ${sessionRequests2} • in ${sessionInTokens2} • out ${sessionOutTokens2} • $${((sessionCostCents2||0)/100).toFixed(2)}`
                            ];
                            navigator.clipboard.writeText(rows.join('\n'));
                            setToast({ show:true, type:'success', message:'Diagnostics copied.'});
                          } catch {}
                        }}>Copy diagnostics</button>
                      </div>
                    </div>
                  )}
                  {attachments && attachments.length > 0 && (
                    <>
                    {showAttachDrawer && (
                      <div role="region" aria-label="Attachment options" style={{ marginTop:6, border:'1px solid #e5e7eb', borderRadius:8, padding:8, background:'#fafafa' }}>
                        {attachments.map((f, idx) => (
                          <div key={idx} style={{ borderBottom:'1px solid #eee', padding:'8px 0', display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
                            <div>
                              <div style={{ fontWeight:600, maxWidth:420, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={f.name}>{f.name}</div>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginTop:6, alignItems:'center' }}>
                                <label title="Include in this request"><input type="checkbox" checked={f.include !== false} onChange={e=>setAttachments(prev => prev.map((a,i)=> i===idx? { ...a, include: e.target.checked } : a))} /> Include</label>
                                <label title="Extract text (PDF/PPTX/Docx)"><input type="checkbox" checked={!!f.useExtract} onChange={e=>setAttachments(prev => prev.map((a,i)=> i===idx? { ...a, useExtract: e.target.checked } : a))} /> Extract</label>
                                <label title={isPaidUser ? 'Use for vision (images/pages/slides)' : 'Vision is a Pro feature'}><input type="checkbox" checked={!!f.useVision} disabled={!isPaidUser} onChange={e=>setAttachments(prev => prev.map((a,i)=> i===idx? { ...a, useVision: e.target.checked } : a))} /> Vision</label>
                                {((f.type||'').includes('pdf') || (f.name||'').toLowerCase().endsWith('.pdf') || (f.name||'').toLowerCase().endsWith('.ppt') || (f.name||'').toLowerCase().endsWith('.pptx')) && (
                                  <>
                                    <label title={!isPaidUser ? 'Advanced page selection is a Pro feature' : undefined}>Start
                                      <input type="number" min={1} step={1} disabled={!isPaidUser} value={Number(f.pageStart||1)} onChange={e=>{
                                        const v = Math.max(1, parseInt(e.target.value||'1',10));
                                        setAttachments(prev => prev.map((a,i)=> i===idx? { ...a, pageStart: v } : a));
                                      }} style={{ width:64, marginLeft:6 }} />
                                    </label>
                                    <label title={!isPaidUser ? 'Advanced page selection is a Pro feature' : undefined}>End
                                      <input type="number" min={1} step={1} disabled={!isPaidUser} value={Number(f.pageEnd||3)} onChange={e=>{
                                        const v = Math.max(1, parseInt(e.target.value||'3',10));
                                        setAttachments(prev => prev.map((a,i)=> i===idx? { ...a, pageEnd: v } : a));
                                      }} style={{ width:64, marginLeft:6 }} />
                                    </label>
                                  </>
                                )}
                              </div>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <button onClick={() => setAttachments(prev => (prev||[]).filter((_,i)=>i!==idx))} style={{ border:'1px solid #e5e7eb', background:'#fff', cursor:'pointer', borderRadius:6, padding:'2px 8px' }} aria-label={`Remove ${f.name}`}>Remove</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </>
                  )}
				</div>
	  
				<div className="bottom-main">
				  <div className="io-thread" ref={threadRef}>
					{pending && (
					  <div className={`io-block ai`}>
						<div className="io-label" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						  <span>{`${providerKey}${model ? ` (${model})` : ''}`}</span>
						  <div className="loader-ball-37"><div></div><div></div><div></div><div></div></div>
						</div>
					  </div>
					)}
					{thread.map((t, idx) => (
					  <div key={idx} className={`io-block ${t.role}`}>
						<div className="io-label">{t.label || (t.role === 'user' ? 'You' : 'AI')}</div>
						<pre className="io-content">{t.text}</pre>
					  </div>
					))}
				  </div>
				  {(!providers?.length || (providers.every(p => (p.allowed || []).length === 0))) && (
					<div className="hint">No models loaded. In dev, set user-secrets. In prod, set env vars. Then refresh.</div>
				  )}
				</div>
			  </div>
	  
			  {showRecall && (
        <div className="modal-backdrop" onClick={() => setShowRecall(false)}>
          <div className="app-modal" onClick={e => e.stopPropagation()}>
            <div className="app-modal-header">
              <strong>Recall Saved I/O</strong>
              <button className="close" onClick={() => setShowRecall(false)}>×</button>
            </div>
            <div className="app-modal-body">
              {savedIos.length === 0 && <div className="hint">No saved I/O yet.</div>}
              {savedIos.length > 0 && (
                <ul className="io-list">
                  {savedIos.map(item => (
                    <li key={item.id} className="io-list-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                      <div className="io-list-title" style={{ flex: 1, minWidth: 0 }}>{item.title}</div>
                      <div className="io-list-actions" style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => loadSavedAsync(item.id)}>Load</button>
                        <button onClick={() => { setRenameId(item.id); setRenameTitle(item.title || ''); setShowRecall(false); setShowRenameModal(true); }}>Rename</button>
                        <button onClick={() => deleteSaveAsync(item.id)}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  </div>

  {showSaveModal && (
    <div className="modal-backdrop" onClick={() => setShowSaveModal(false)}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Save Session</strong>
          <button className="close" onClick={() => setShowSaveModal(false)}>×</button>
        </div>
        <div className="app-modal-body">
          <label>File name</label>
          <input type="text" value={saveTitle} onChange={e => setSaveTitle(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button className="primary" onClick={async () => {
              try {
                await performSaveAsync(saveTitle);
                setShowSaveModal(false);
                resetAioBox();
              } catch (e) {
                setToast({ show: true, type: 'error', message: e.message || 'Save failed.' });
              }
            }}>Save</button>
            <button onClick={() => { setShowResetConfirm(true); }}>Reset</button>
            <button onClick={() => setShowSaveModal(false)}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )}

  {showSaveWarningModal && (
    <div className="modal-backdrop" onClick={() => { setShowSaveWarningModal(false); setPendingSaveTitle(null); }}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Archived Messages Warning</strong>
          <button className="close" onClick={() => { setShowSaveWarningModal(false); setPendingSaveTitle(null); }}>×</button>
        </div>
        <div className="app-modal-body">
          <p>You have {archivedCount} archived messages. Only the last {Number(chatContextWindow || 15)} message pairs will be saved. Earlier messages will be lost.</p>
          <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
            Teams and Enterprise tiers preserve full conversation history with unlimited saved conversations.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="primary" onClick={async () => {
              try {
                await performSaveAsync(pendingSaveTitle, true); // Skip warning on retry
                setShowSaveWarningModal(false);
                setShowSaveModal(false);
                setPendingSaveTitle(null);
                resetAioBox();
              } catch (e) {
                setToast({ show: true, type: 'error', message: e.message || 'Save failed.' });
              }
            }}>Save Anyway</button>
            <button onClick={() => { setShowSaveWarningModal(false); setPendingSaveTitle(null); }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )}

  {showResetConfirm && (
    <div className="modal-backdrop" onClick={() => setShowResetConfirm(false)}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Reset AI Input/Output?</strong>
          <button className="close" onClick={() => setShowResetConfirm(false)}>×</button>
        </div>
        <div className="app-modal-body" style={{ fontSize: 16 }}>
          The AI page resets on open. You have unsaved work. Are you sure you want to reset the AIO box?
        </div>
        <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end', padding: '0 20px 20px' }}>
          <button className="primary" onClick={() => {
            resetAioBox();
            setTemperature(0.7);
            setTopP(0.9);
            setShowResetConfirm(false);
            setShowSaveModal(false);
            setToast({ show: true, type: 'success', message: 'Reset.' });
          }}>Reset</button>
          <button onClick={() => setShowResetConfirm(false)}>Cancel</button>
        </div>
      </div>
    </div>
  )}

  {showRenameModal && (
    <div className="modal-backdrop" onClick={() => { setShowRenameModal(false); setRenameId(null); }}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Rename Saved I/O</strong>
          <button className="close" onClick={() => { setShowRenameModal(false); setRenameId(null); }}>×</button>
        </div>
        <div className="app-modal-body">
          <label>New name</label>
          <input type="text" autoFocus value={renameTitle} onChange={e => setRenameTitle(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button className="primary" onClick={() => renameSaveAsync(renameId, renameTitle)}>Rename</button>
            <button onClick={() => { setShowRenameModal(false); setRenameId(null); }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )}

  {showChatModal && (
    <div className="modal-backdrop" onClick={() => setShowChatModal(false)}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Switch to Chat Mode</strong>
          <button className="close" onClick={() => setShowChatModal(false)}>×</button>
        </div>
        <div className="app-modal-body">
          <div style={{ marginBottom: 8 }}>
            <div>⚠️ Uses significantly more tokens</div>
            <div>💬 Allows back-and-forth conversation</div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label>Chat budget (% of current balance)</label>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="range" min={0} max={90} step={5} value={Number(chatPct||0)} onChange={e=>setChatPct(Number(e.target.value))} style={{ flex:1 }} />
              <div style={{ width: 160, textAlign:'right' }}>
                {`${Number(chatPct||0)}% → CCO $${(((Number(chatBalanceCents||0)) * (1 - (Number(chatPct||0)/100)))/100).toFixed(2)}`}
              </div>
            </div>
            <div style={{ fontSize: 12, color:'#555' }}>CCO is the remaining balance floor during Chat. Edit anytime.</div>
          </div>
          <div style={{ marginTop: 16 }}>
            <label>Context Window</label>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="range" min={5} max={30} step={1} value={Number(chatContextWindow||15)} onChange={e=>setChatContextWindow(Number(e.target.value))} style={{ flex:1 }} />
              <div style={{ width: 200, textAlign:'right', fontSize: 12 }}>
                {(() => {
                  const pairs = Number(chatContextWindow || 15);
                  const messages = pairs * 2;
                  // Estimate tokens: rough approximation (1 token ≈ 4 characters)
                  // Average message length estimate: 100 chars per message (conservative)
                  const estimatedTokens = Math.round((messages * 100) / 4);
                  return `Last ${pairs} message pairs (~${estimatedTokens.toLocaleString()} tokens)`;
                })()}
              </div>
            </div>
            <div style={{ fontSize: 12, color:'#555' }}>Number of recent message pairs sent to AI. Older messages are archived but not sent.</div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
            <button className="primary" onClick={confirmChatMode}>Enable Chat</button>
            <button onClick={() => setShowChatModal(false)}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )}

	{/* Help Modal for selecting Category/Style/Outcome */}
	<HelpModal isOpen={pgHelpOpen} onClose={() => setPgHelpOpen(false)} onSelectCategory={handlePgHelpSelect} />

	{/* Prompt Generator Modal */}
	<PromptGeneratorModal
		isOpen={showPromptGeneratorModal}
		onClose={() => setShowPromptGeneratorModal(false)}
		onDropIn={(promptText) => {
			if (promptText && promptText.trim()) {
				setPrompt(promptText.trim());
			}
		}}
	/>

	{showLeaveConfirm && (
		<div className="modal-backdrop" onClick={() => setShowLeaveConfirm(false)}>
			<div className="app-modal" onClick={e => e.stopPropagation()}>
				<div className="app-modal-header">
					<strong>Leave AI Page?</strong>
					<button className="close" onClick={() => setShowLeaveConfirm(false)}>×</button>
				</div>
				<div className="app-modal-body" style={{ fontSize: 16 }}>
					The AI page resets on open. You have unsaved work. Are you sure you want to leave the AI page?
				</div>
				<div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end', padding: '0 20px 20px' }}>
                    <button className="primary" onClick={() => {
						const to = pendingNavTo || '/';
						setShowLeaveConfirm(false);
						try { sessionStorage.removeItem('ai_nav_confirm_to'); } catch {}
                        try { allowUnloadRef.current = true; } catch {}
                        window.location.href = to;
					}}>Leave</button>
					<button onClick={() => setShowLeaveConfirm(false)}>Stay</button>
				</div>
			</div>
		</div>
	)}

  {showOverride && (
    <div onClick={() => setShowOverride(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ background:'#fff', borderRadius:8, padding:16, width:'min(400px, 92vw)', boxShadow:'0 12px 28px rgba(0,0,0,0.18)', zIndex:200001 }}>
        <h4 style={{ marginTop: 0, marginBottom: 8 }}>Advanced: Token Limit Override</h4>
        <div style={{ display:'grid', gap:10 }}>
          <label style={{ fontSize: 12, color: '#555' }}>Max output tokens
            <input type="number" min={1024} step={1024} value={ovTokens} onChange={e => setOvTokens(Math.max(1024, parseInt(e.target.value||'0',10)||8192))} className="form-input" />
          </label>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <label style={{ fontSize: 12, color:'#555' }}><input type="radio" checked={ovMode==='time'} onChange={()=>setOvMode('time')} /> Expires in (hours)</label>
            <input type="number" min={1} max={168} value={ovHours} disabled={ovMode!=='time'} onChange={e=>setOvHours(Math.min(168, Math.max(1, parseInt(e.target.value||'0',10)||48)))} className="form-input" style={{ width: 110 }} />
          </div>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <label style={{ fontSize: 12, color:'#555' }}><input type="radio" checked={ovMode==='uses'} onChange={()=>setOvMode('uses')} /> Remaining uses</label>
            <input type="number" min={1} max={50} value={ovUses} disabled={ovMode!=='uses'} onChange={e=>setOvUses(Math.min(50, Math.max(1, parseInt(e.target.value||'0',10)||5)))} className="form-input" style={{ width: 110 }} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
            <button className="action-button secondary" onClick={()=>setShowOverride(false)} disabled={ovPending}>Cancel</button>
            <button className="action-button primary" disabled={ovPending} onClick={async ()=>{
              try {
                setOvPending(true);
                const t = authService.getToken && authService.getToken();
                const body = { perRequestMaxOutputTokensOverride: ovTokens, overrideRemainingUses: (ovMode==='uses'? ovUses : null), overrideExpiresUtc: (ovMode==='time'? new Date(Date.now()+ovHours*3600*1000).toISOString() : null) };
                const r = await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body), credentials:'include' });
                if (!r.ok) throw new Error('Failed to save');
                setShowOverride(false);
                setToast && setToast({ show:true, type:'success', message:'Override saved.' });
              } catch {
                setToast && setToast({ show:true, type:'error', message:'Failed to save override.' });
              } finally { setOvPending(false); }
            }}>Save</button>
          </div>
          <div style={{ marginTop:6, display:'flex', justifyContent:'flex-end' }}>
            <button className="action-button" disabled={ovPending} onClick={async ()=>{
              try {
                setOvPending(true);
                const t = authService.getToken && authService.getToken();
                const body = { perRequestMaxOutputTokensOverride: null, overrideRemainingUses: null, overrideExpiresUtc: null };
                const r = await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body), credentials:'include' });
                if (!r.ok) throw new Error('Failed to clear');
                setToast && setToast({ show:true, type:'success', message:'Override cleared.' });
              } catch {
                setToast && setToast({ show:true, type:'error', message:'Failed to clear override.' });
              } finally { setOvPending(false); }
            }}>Clear Override</button>
          </div>
        </div>
      </div>
    </div>
  )}

  {showPii && (
    <div onClick={() => { setShowPii(false); setPiiPending(null); }} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div ref={piiDialogRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="pii-modal-title" aria-describedby="pii-modal-desc" style={{ background:'#fff', borderRadius:8, padding:16, width:'min(520px, 92vw)', boxShadow:'0 12px 28px rgba(0,0,0,0.18)', zIndex:200001 }}>
        <h3 id="pii-modal-title" style={{ marginTop:0, marginBottom:8 }}>Possible PII detected</h3>
        <p id="pii-modal-desc" style={{ margin:'4px 0 10px', color:'#374151', fontSize:13 }}>Review the following items. You can redact them automatically or proceed as-is.</p>
        <ul style={{ paddingLeft:16, maxHeight:180, overflowY:'auto' }}>
          {piiFindings.map((f,i)=>(<li key={i} style={{ fontFamily:'monospace', fontSize:12 }}>{f}</li>))}
        </ul>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:10 }}>
          <button className="action-button secondary" onClick={()=>{ setShowPii(false); setPiiPending(null); }} aria-label="Cancel PII review">Cancel</button>
          <button className="action-button" disabled={pending} onClick={()=>{ setShowPii(false); setPiiBypass(true); setTimeout(()=> onSubmit(true), 0); }} aria-label="Send without redaction">Send as-is</button>
          <button className="action-button primary" disabled={pending} onClick={()=>{
            try {
              const found = piiFindings;
              setPrompt(p => redactText(p, found));
              setAttachments(prev => (prev||[]).map(a => ({ ...a, text: redactText(a?.text||'', found) })));
              setShowPii(false);
              setPiiPending(null);
              setSendRedactPii(true);
              setPiiBypass(true);
              setTimeout(()=> onSubmit(true), 0);
            } catch { setShowPii(false); setPiiPending(null); }
          }} aria-label="Redact detected PII and send">Redact & Send</button>
        </div>
      </div>
    </div>
  )}

  {showModeration && (
    <div onClick={() => { /* require explicit choice; do not close on backdrop */ }} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div ref={modDialogRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="mod-modal-title" aria-describedby="mod-modal-desc" style={{ background:'#fff', borderRadius:8, padding:16, width:'min(560px, 92vw)', boxShadow:'0 12px 28px rgba(0,0,0,0.18)', zIndex:200001 }}>
        <h3 id="mod-modal-title" style={{ marginTop:0, marginBottom:8 }}>Moderation warning</h3>
        <p id="mod-modal-desc" style={{ margin:'4px 0 10px', color:'#374151', fontSize:13 }}>{modMessage || 'Potential policy issues detected. Proceed with caution.'}</p>
        <ul style={{ paddingLeft:16, maxHeight:180, overflowY:'auto' }}>
          {modCategories.map((c, i) => (
            <li key={i} style={{ fontSize:12 }}>
              <strong>{c.name}</strong> — action: {c.action}{typeof c.score==='number' ? ` (score ${(c.score*100).toFixed(0)}%)` : ''}{c.note ? ` — ${c.note}` : ''}
            </li>
          ))}
          {(!modCategories || modCategories.length===0) && <li style={{ fontSize:12 }}>General risk detected</li>}
        </ul>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:10 }}>
          <button className="action-button secondary" onClick={()=>{ setShowModeration(false); setPending(false); }} aria-label="Cancel send">Cancel</button>
          {modDecision !== 'block' && (
            <>
              <button className="action-button" disabled={pending} onClick={()=>{ setShowModeration(false); setModBypassOnce(true); setDoNotStore(true); setTimeout(()=> onSubmit(false), 0); }} aria-label="Proceed without changes">Send as-is</button>
              <button className="action-button primary" disabled={pending} onClick={()=>{
                try {
                  // Leverage existing PII redaction as a conservative redact-all step
                  setPrompt(p => redactText(p, piiFindings && piiFindings.length ? piiFindings : []));
                  setAttachments(prev => (prev||[]).map(a => ({ ...a, text: redactText(a?.text||'', piiFindings && piiFindings.length ? piiFindings : []) })));
                } finally {
                  setShowModeration(false);
                  setModBypassOnce(true);
                  setDoNotStore(true);
                  setTimeout(()=> onSubmit(false), 0);
                }
              }} aria-label="Redact content and send">Redact & Send</button>
            </>
          )}
          {modDecision === 'block' && (
            <span style={{ color:'#b91c1c', alignSelf:'center', marginRight:'auto' }}>This content is blocked and cannot be sent.</span>
          )}
        </div>
      </div>
    </div>
  )}

  <div aria-live="polite" aria-atomic="true" style={{ position:'fixed', left:-10000, width:1, height:1, overflow:'hidden' }}>{toast && toast.message}</div>
  <Toast
    message={toast.message}
    type={toast.type}
    show={toast.show}
    onClose={() => setToast(prev => ({ ...prev, show: false }))}
  />
  <Toast
    message={confirmToast.message}
    type="warning"
    show={confirmToast.show}
    persist={true}
    actionLabel="Confirm"
    onAction={confirmToast.onConfirm}
    secondaryActionLabel="Cancel"
    onSecondaryAction={confirmToast.onCancel}
    onClose={() => setConfirmToast(prev => ({ ...prev, show: false }))}
  />

	{/* Multi-send modal */}
	{showMultiSend && (
	  <div onClick={()=>{ if (!msPending) { setShowMultiSend(false); setMsSelected([]);} }} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200000, display:'flex', alignItems:'center', justifyContent:'center' }}>
	    <div onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true" style={{ width:700, maxWidth:'92vw', background:'#fff', color:'#333', borderRadius:10, boxShadow:'0 12px 28px rgba(0,0,0,0.18)', padding:16 }}>
	      <div style={{ fontSize:18, fontWeight:700, textAlign:'center', marginBottom:10 }}>Send to Multiple AI Models</div>
	      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', margin:'4px 0 8px' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <input type="checkbox" checked={msSelected.length>=4} onChange={(e)=>{
            if (e.target.checked) {
              const order = ['OpenAI','Anthropic','Gemini','Together','Mistral'];
              const rows = [0,1,2];
              const all = [];
              const defaultModels = {
                'together': ['meta-llama/Llama-4-Scout-17B-16E-Instruct', 'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8'],
                'openai': ['gpt-4o-mini', 'gpt-4o-mini-2024-07-18'],
                'anthropic': ['claude-haiku-4-5-20251001', 'claude-sonnet-4-5-20250929', 'claude-opus-4-1-20250805'],
                'gemini': ['gemini-2.5-flash', 'gemini-2.5-pro'],
                'mistral': ['mistral-large-latest']
              };
              
              const findProvider = (name) => {
                const target = name.toLowerCase();
                let found = (providers||[]).find(pp => {
                  const key = String(pp.key || pp.name || '').toLowerCase();
                  return key === target || 
                         (target === 'together' && key.includes('together')) ||
                         (target === 'openai' && key.includes('openai')) ||
                         (target === 'anthropic' && key.includes('anthropic')) ||
                         (target === 'gemini' && key.includes('gemini')) ||
                         (target === 'mistral' && key.includes('mistral'));
                });
                
                // Fallback to defaults if not found
                if (!found && defaultModels[target]) {
                  found = {
                    key: name,
                    name: name,
                    allowed: defaultModels[target],
                    allowedModels: defaultModels[target]
                  };
                }
                
                return found;
              };
              const getAllowed = (p) => {
                if (!p) return [];
                if (Array.isArray(p.allowed)) return p.allowed;
                if (Array.isArray(p.allowedModels)) return p.allowedModels;
                return [];
              };
              rows.forEach(r => {
                order.forEach(name => {
                  const p = findProvider(name);
                  const mlist = getAllowed(p);
                  const m = mlist[r];
                  if (m) all.push({ provider:name, model:m, label:m });
                });
              });
              setMsSelected(all.slice(0,4));
            } else {
              setMsSelected([]);
            }
          }} />
	          <span>Select All / Deselect All</span>
	        </label>
	        <div style={{ fontSize:12, color:'#666' }}>{msSelected.length} selected</div>
	      </div>
	      <div style={{ height:1, background:'#e5e7eb', margin:'4px 0 8px' }} />
	      {/* 5 columns x 3 rows grid */}
	      {(() => {
	        // Default models for providers (fallback if not in providers list)
        const defaultProviderModels = {
          'together': ['meta-llama/Llama-4-Scout-17B-16E-Instruct', 'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8'],
	          'openai': ['gpt-4o-mini', 'gpt-4o-mini-2024-07-18'],
	          'anthropic': ['claude-haiku-4-5-20251001', 'claude-sonnet-4-5-20250929', 'claude-opus-4-1-20250805'],
	          'gemini': ['gemini-2.5-flash', 'gemini-2.5-pro'],
	          'mistral': ['mistral-large-latest']
	        };
	        
	        // Helper function to find provider by name (case-insensitive, handles variations)
	        const findProvider = (name) => {
	          const target = name.toLowerCase().trim();
	          
	          // First try exact match (case-insensitive)
	          let found = (providers||[]).find(pp => {
	            const key = String(pp.key || pp.name || '').toLowerCase().trim();
	            return key === target;
	          });
	          
	          // If not found, try substring matching for Together specifically
          if (!found && target === 'together') {
	            found = (providers||[]).find(pp => {
	              const key = String(pp.key || pp.name || '').toLowerCase().trim();
              return key.includes('together') || key.includes('llama');
	            });
          }
          // Alias: Llama column may map to provider keys containing "llama"
          if (!found && target === 'llama') {
            found = (providers||[]).find(pp => {
              const key = String(pp.key || pp.name || '').toLowerCase().trim();
              return key.includes('llama');
            });
	          }
	          
	          // If still not found, create a fallback provider with default models
	          if (!found && defaultProviderModels[target]) {
	            console.warn('[Multi-Send] Provider not found in list, using defaults for:', name);
	            found = {
	              key: name,
	              name: name,
	              allowed: defaultProviderModels[target],
	              allowedModels: defaultProviderModels[target]
	            };
	          }
	          
	          // Debug logging
	          if (!found) {
	            console.warn('[Multi-Send] Provider not found for:', name, 'Searching for:', target);
	            console.log('[Multi-Send] Available providers:', (providers||[]).map(p => ({ 
	              key: p.key, 
	              name: p.name,
	              allowedCount: (p.allowed || p.allowedModels || []).length
	            })));
	          }
	          
	          return found;
	        };
	        
	        // Helper to get allowed models array
	        const getAllowed = (p) => {
	          if (!p) return [];
	          if (Array.isArray(p.allowed)) return p.allowed;
	          if (Array.isArray(p.allowedModels)) return p.allowedModels;
	          return [];
	        };
	        
	        return (
	          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', columnGap:12, rowGap:8 }}>
	            {/* Headers */}
	            {['OpenAI','Anthropic','Gemini','Together','Mistral'].map(h => (
	              <div key={h} style={{ fontWeight:700, fontSize:14, textDecoration:'underline' }}>{h}</div>
	            ))}
	            {/* Row 1 */}
	            {['OpenAI','Anthropic','Gemini','Together','Mistral'].map(name => {
	              const p = findProvider(name);
	              const allowed = getAllowed(p);
	              const m = allowed[0] || null;
	              
	              // Debug Together specifically
	              if (name === 'Together' && !m) {
	                console.log('[Multi-Send] Together provider debug:', {
	                  found: !!p,
	                  provider: p ? { key: p.key, name: p.name, allowed: p.allowed, allowedModels: p.allowedModels } : null,
	                  allowedArray: allowed,
	                  allowedLength: allowed.length
	                });
	              }
	              
	              if (!m) return (<div key={name+':r0'} />);
	              const opt = { provider:name, model:m, label:m };
	              return (
	                <label key={name+':'+m} style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
	                  <input type="checkbox" checked={msSelected.some(s=>s.provider===opt.provider && s.model===opt.model)} onChange={(e)=>{
	                    setMsSelected(prev => {
	                      const exists = prev.some(s=>s.provider===opt.provider && s.model===opt.model);
	                      if (e.target.checked) {
	                        if (exists) return prev;
	                        if (prev.length >= 4) return prev;
	                        return [...prev, opt];
	                      } else {
	                        return prev.filter(s=> !(s.provider===opt.provider && s.model===opt.model));
	                      }
	                    });
	                  }} />
	                  {opt.label}
	                </label>
	              );
	            })}
	            {/* Row 2 */}
	            {['OpenAI','Anthropic','Gemini','Together','Mistral'].map(name => {
	              const p = findProvider(name);
	              const allowed = getAllowed(p);
	              const m = allowed[1] || null;
	              if (!m) return (<div key={name+':r1'} />);
	              const opt = { provider:name, model:m, label:m };
	              return (
	                <label key={name+':'+m} style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
	                  <input type="checkbox" checked={msSelected.some(s=>s.provider===opt.provider && s.model===opt.model)} onChange={(e)=>{
	                    setMsSelected(prev => {
	                      const exists = prev.some(s=>s.provider===opt.provider && s.model===opt.model);
	                      if (e.target.checked) {
	                        if (exists) return prev;
	                        if (prev.length >= 4) return prev;
	                        return [...prev, opt];
	                      } else {
	                        return prev.filter(s=> !(s.provider===opt.provider && s.model===opt.model));
	                      }
	                    });
	                  }} />
	                  {opt.label}
	                </label>
	              );
	            })}
	            {/* Row 3 */}
	            {['OpenAI','Anthropic','Gemini','Together','Mistral'].map((name, idx) => {
	              const p = findProvider(name);
	              const allowed = getAllowed(p);
	              const m = allowed[2] || null;
	              if (!m) return (<div key={name+':r2'} />);
	              const opt = { provider:name, model:m, label:m };
	              return (
	                <label key={name+':'+m} style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
	                  <input type="checkbox" checked={msSelected.some(s=>s.provider===opt.provider && s.model===opt.model)} onChange={(e)=>{
	                    setMsSelected(prev => {
	                      const exists = prev.some(s=>s.provider===opt.provider && s.model===opt.model);
	                      if (e.target.checked) {
	                        if (exists) return prev;
	                        if (prev.length >= 4) return prev;
	                        return [...prev, opt];
	                      } else {
	                        return prev.filter(s=> !(s.provider===opt.provider && s.model===opt.model));
	                      }
	                    });
	                  }} />
	                  {opt.label}
	                </label>
	              );
	            })}
	          </div>
	        );
	      })()}
	      <div style={{ height:1, background:'#e5e7eb', margin:'12px 0 8px' }} />
	      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
	        <button onClick={()=>{ if (!msPending) { setShowMultiSend(false); setMsSelected([]); } }} style={{ border:'1px solid #e5e7eb', background:'#f3f4f6', color:'#111827', borderRadius:6, padding:'8px 12px' }}>Cancel</button>
        <button disabled={msSelected.length===0 || msPending} onClick={async ()=>{
	          try {
	            setMsPending(true);
	            setShowMultiSend(false);
	            const list = msSelected.slice(0,4);
	            for (let i=0;i<list.length;i++){
	              const it = list[i];
              try {
                if (i>0) { setModBypassOnce(true); setPiiBypass(true); }
                await onSubmit(i>0, { provider: it.provider, model: it.model });
	              } catch {}
	            }
	          } finally { setMsPending(false); setMsSelected([]); }
        }} style={{ flex:1, textAlign:'center', border:'1px solid #667eea', background:'#c99edb', color:'#3730a3', borderRadius:6, padding:'8px 12px', opacity:(msSelected.length===0||msPending)?0.6:1 }}>Send</button>
	      </div>
	    </div>
	  </div>
	)}
</>
	  );
	};
export default AiPlayground;

// eslint-disable-next-line no-unused-vars
function UsageChip({ tick }) {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [tries, setTries] = React.useState(0);
  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const t = authService.getToken && authService.getToken();
      const r = await fetch('/api/ai/usage', { credentials: 'include', headers: t ? { Authorization: `Bearer ${t}` } : {} });
      const ct = r.headers.get('content-type') || '';
      if (r.ok && ct.includes('application/json')) {
        const j = await r.json();
        setData(j);
        setTries(0);
      } else {
        setTries(x => x + 1);
      }
    } catch {
      setTries(x => x + 1);
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { if (tick) load(); }, [tick, load]);
  React.useEffect(() => {
    if (!data && tries > 0 && tries <= 3) {
      const id = setTimeout(load, 800);
      return () => clearTimeout(id);
    }
  }, [tries, data, load]);
  const remaining = data?.remainingTokens;
  return (
    <span className="chip usage" title="Remaining tokens (PaidUser)">
      {loading ? 'Remaining: …' : `Remaining: ${typeof remaining === 'number' ? remaining.toLocaleString() : '—'}`}
    </span>
  );
}

function BalanceChip({ tick }) {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const t = authService.getToken && authService.getToken();
      const r = await fetch('/api/billing/balance', { credentials: 'include', cache: 'no-store', headers: t ? { Authorization: `Bearer ${t}` } : {} });
      const ct = r.headers.get('content-type') || '';
      const raw = await r.text();
      if (r.ok) {
        try { setData(ct.includes('application/json') && raw ? JSON.parse(raw) : null); } catch {}
      }
    } catch {}
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { if (tick) load(); }, [tick, load]);
  const cents = data?.balanceCents;
  const micro = data?.balanceMicroUsd;
  let displayDollars = null;
  let exactDollars = null;
  if (typeof micro === 'number' && micro >= 0) {
    try {
      const val = micro/1_000_000;
      exactDollars = val.toFixed(6);
      displayDollars = val.toFixed(2);
    } catch {}
  }
  if (displayDollars == null && typeof cents === 'number') {
    const val = cents/100;
    displayDollars = val.toFixed(2);
    exactDollars = exactDollars ?? displayDollars;
  }
  return (
    <span className="chip remaining" title={exactDollars != null ? `Exact: $${exactDollars}` : '$Remaining'}>
      {loading ? '$Remaining: …' : `$Remaining: ${displayDollars != null ? `$${displayDollars}` : '—'}`}
    </span>
  );
}